import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import CommonInput from '../../component/form/common-input.component';
import type { SelectOption } from '../../component/form/base-input.component';
import SelectInputMultiple, { type Option } from '../../component/form/select-input-multiple';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import { AntSwitch } from '../../component/switch/IOSSwitch';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
/** ic:baseline-delete-forever */
import { ReactComponent as BaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import {
    ALLERGEN,
    INGREDIENTS,
    defaultI18n,
    getI18nMap,
    type DishItemI18nShape,
} from '../../static/images/dish-base-data';
import { ITEM_TYPE, type MenuDish } from '../../models';
import { apiManage_rbp } from '../../request';
import type { PatchDishesParams } from '../../request/api/rbp/menu';
import {
    buildCloudDishLibraryBatchUpdateDishBody,
    buildCloudDishLibraryUpdateDishBody,
    type CloudDishCondimentGroupSource,
    type CloudDishLibraryBatchUpdateDishPatch,
    type CloudDishLibraryCourseGroup,
    type CloudDishLibraryCreateDishBody,
    type CloudDishLibraryMenuDetailDish,
    resolveCloudDishCondimentGroupForCreate,
} from '../../request/api/rbp/cloud-dish-library';
import setLoading from '../../component/linear-determinate';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import type { MenuLibraryDisplayLang, MenuLibraryManagementState } from './menu-library-management-state';
import { getCloudDishIdFromMenuDish } from './menu-library-management-state';
import { mergeCloudCreateDialogDishRowAfterUpdate } from '../../utils/menu-library-cloud-create-dish-merge';
import { getCondimentGroupsRawAfterRefFetch } from './menu-library-condiment-ref-fetch';
import { MenuLanguageMenuButton } from './menu-library-language-menu-button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { MenuLibraryCourseGroupDialog } from './menu-library-course-group-dialog.component';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { buildRestaurantStaticImageDisplayUrl } from '../../utils/restaurant-static-image-url';
import {
    buildItemI18nMergedFromDish,
    resolveDishFormNamesFromHydrated,
} from '../../utils/menu-library-dish-i18n-hydrate';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import './scss/menu-library-add-dish-dialog.style.scss';

/** 与 `ITEM_TYPE` 中套餐一致（`menu-dish.entity` value: 3） */
const SET_MEAL_ITEM_TYPE = 3;
/** 计时菜品（`menu-dish.entity` value: 7），与 RBP2 `classify-menu-detail` 一致 */
const TIMED_DISH_ITEM_TYPE = 7;

/** 批量设置：下拉与三态开关的「不修改」占位值（勿与业务 id 冲突） */
export const MENU_LIB_ADD_DISH_BATCH_NO_CHANGE = '__NO_CHANGE__' as const;

const NOT_SPECIFIED = 'menu_and_dishes_page_not_specified';

/** 新增菜品弹窗打开时菜品名称 1、名称 2 的默认占位 */
const DEFAULT_NEW_DISH_NAME = 'Unnamed';

function buildInitialItemI18n(): DishItemI18nShape {
    const base = _.cloneDeep(defaultI18n);
    _.set(base, ['default', 'name1'], DEFAULT_NEW_DISH_NAME);
    _.set(base, ['default', 'name2'], DEFAULT_NEW_DISH_NAME);
    return base;
}

/** 过敏源 / 其他标识：缓存里多为 `string[]`，接口偶为 `*` 拼接字符串 */
function parseDetailMultiKeysFromDish(v: unknown): string[] {
    if (Array.isArray(v)) {
        return v.filter((x): x is string => typeof x === 'string' && x.length > 0);
    }
    if (typeof v === 'string' && v.trim()) {
        return v.split('*').filter(Boolean);
    }
    return [];
}

const DISH_TYPE_ITEMS: SelectOption[] = ITEM_TYPE.map((item) => ({
    value: String(item.value),
    label: item.label,
}));

const SPECIAL_ATTR_ITEMS: SelectOption[] = [
    { value: '0', label: i18n.t('print_setting_page_none0') },
    { value: '1', label: i18n.t('menu_and_dishes_page_chef_recommendation') },
    { value: '2', label: i18n.t('menu_and_dishes_page_new_menu_recommendation') },
    { value: '3', label: i18n.t('menu_and_dishes_page_has_discount') },
];

function numOrDefault(raw: string, fallback: number): number {
    const t = raw.trim();
    if (t === '') {
        return fallback;
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : fallback;
}

function parseSelectInt(raw: string, unset: number): number {
    if (raw === '' || raw === String(unset)) {
        return unset;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : unset;
}

/** 非负小数输入：禁止负号与非法字符，失焦时规范为 ≥0 */
function sanitizeNonNegativeDecimalInput(raw: string): string {
    if (raw === '') {
        return '';
    }
    const t = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
    const parts = t.split('.');
    const head = parts[0] ?? '';
    const tail = parts.length > 1 ? `.${parts.slice(1).join('').replace(/\./g, '')}` : '';
    return `${head}${tail}`;
}

function clampNonNegativeDecimalString(s: string): string {
    if (s.trim() === '') {
        return '';
    }
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) {
        return '0';
    }
    return String(n);
}

/** 非负整数 */
function sanitizeNonNegativeIntInput(raw: string): string {
    if (raw === '') {
        return '';
    }
    return raw.replace(/\D/g, '');
}

function clampNonNegativeIntString(s: string): string {
    if (s.trim() === '') {
        return '';
    }
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n < 0) {
        return '0';
    }
    return String(n);
}

const nameTextFieldSx = {
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiOutlinedInput-root': {
        background: 'transparent',
        padding: 0,
    },
    '& .MuiInputBase-input': {
        padding: '14px 0 0',
        textAlign: 'left' as const,
        fontWeight: 700,
        color: 'var(--fill-primary-300)',
    },
    '& .MuiInputLabel-root': {
        textAlign: 'left' as const,
        transformOrigin: 'top left',
        left: 0,
        color: 'var(--text-dark)',
    },
    /** outlined small 默认 translateX(14px)，改为 0 使 label 与输入区左对齐 */
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
        transform: 'translate(0, 9px) scale(1)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'var(--text-dark)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        color: 'var(--text-dark)',
        transform: 'translate(0, -9px) scale(0.75)',
    },
};

/** 菜单库 UI 语言 → RBP `item_i18n` 顶层键（与 classify-menu-detail `selectedI18n` 一致） */
function rbpLangKeyFromMenuLib(lang: MenuLibraryDisplayLang): keyof DishItemI18nShape {
    switch (lang) {
        case 'unspecified':
            return 'default';
        case 'zh':
            return 'ZH';
        case 'en':
            return 'EN';
        case 'it':
            return 'IT';
        case 'sp':
            return 'ES';
        default:
            return 'default';
    }
}

type I18nDishTextKey = 'item_name1' | 'item_name2' | 'item_description' | 'ext_dish_info';

export type MenuLibraryAddDishDialogContentProps = {
    state: MenuLibraryManagementState;
    onClose: () => void;
    /** 编辑套餐图标颜色，默认主题色 300（与图标按钮一致） */
    comboIconColor?: string;
    /**
     * 外部只读预览（如菜单商店 structure 行）：与菜单库「新增/编辑菜品」表单同布局与同套参考数据接口，
     * 不展示保存/删除/套餐编辑，表单不可改（展示语言切换仍可用）。
     */
    standaloneReadonlyDish?: MenuDish | null;
    /** 与 `standaloneReadonlyDish` 配对：变化时重新从该行 hydrate 表单 */
    standaloneHydrateKey?: string;
    /** 仅 standalone 时用于税组/打印类等参考接口；缺省走 `state.resolveRbpStringIdForApi()` */
    standaloneRbpStringId?: string;
};

/**
 * 新建弹窗内「新增菜品」：对接 post_dishes，下拉数据与 `menu-dish-detail` 同源 API
 */
export const MenuLibraryAddDishDialogContent = observer(function MenuLibraryAddDishDialogContent(
    props: MenuLibraryAddDishDialogContentProps,
) {
    const {
        onClose,
        state,
        comboIconColor = 'var(--fill-primary-300)',
        standaloneReadonlyDish = null,
        standaloneHydrateKey = '',
        standaloneRbpStringId,
    } = props;
    /** 模块内所选店国家（与顶栏 LSM 独立） */
    const isItaly = state.isMenuLibraryRestaurantItaly;

    /** 菜单商店等场景：仅展示，不调 post/patch */
    const isStandaloneReadonly = standaloneReadonlyDish != null;
    /** standalone 时视为「弹窗已打开」，以拉取税组等下拉参考数据 */
    const dialogOpenForEffects = isStandaloneReadonly || state.isCreateMenuSubcategoryAddDishDialogOpen;
    const rbpId =
        isStandaloneReadonly && standaloneRbpStringId?.trim()
            ? standaloneRbpStringId.trim()
            : state.resolveRbpStringIdForApi();
    const isBatchMode = !isStandaloneReadonly && state.menuLibraryAddDishDialogMode === 'batch';
    const batchNoChangeLabel = i18n.t('menu_library_batch_field_no_change');

    /** 打开弹窗时拉取模块店（或 standalone 店）国家/货币 */
    useEffect(() => {
        if (!dialogOpenForEffects) {
            return;
        }
        void state.fetchMenuLibraryRestaurantContext({
            stringIdOverride: isStandaloneReadonly ? standaloneRbpStringId : undefined,
        });
    }, [dialogOpenForEffects, isStandaloneReadonly, standaloneRbpStringId, state]);

    const [refLoading, setRefLoading] = useState(true);
    /** 保存菜品请求中：禁用保存按钮，避免重复提交 */
    const [saveLoading, setSaveLoading] = useState(false);
    const [taxItems, setTaxItems] = useState<SelectOption[]>([{ value: '-1', label: i18n.t(NOT_SPECIFIED) }]);
    const [condimentItems, setCondimentItems] = useState<SelectOption[]>([
        { value: '-1', label: i18n.t('print_setting_page_none0') },
    ]);
    /** get_condiments_group 全量结果，供云端 create 拼装 condiment_group */
    const [condimentGroupsRaw, setCondimentGroupsRaw] = useState<CloudDishCondimentGroupSource[]>([]);
    const [printItems, setPrintItems] = useState<SelectOption[]>([{ value: '-1', label: i18n.t('print_setting_page_none0') }]);
    const [unitItems, setUnitItems] = useState<SelectOption[]>([{ value: '', label: i18n.t(NOT_SPECIFIED) }]);
    const [majorItems, setMajorItems] = useState<SelectOption[]>([{ value: '-1', label: i18n.t('print_setting_page_none0') }]);
    const [familyItems, setFamilyItems] = useState<SelectOption[]>([{ value: '-1', label: i18n.t('print_setting_page_none0') }]);

    const [name1, setName1] = useState(DEFAULT_NEW_DISH_NAME);
    const [name2, setName2] = useState(DEFAULT_NEW_DISH_NAME);
    /** 与详情/新建一级菜单名称独立：仅本弹窗内菜品展示语言 */
    const [addDishDisplayLang, setAddDishDisplayLang] = useState<MenuLibraryDisplayLang>('unspecified');
    /** 与 RBP2 `item_i18n` 一致：名称1/2、备注、分量说明按语言分栏 */
    const [itemI18n, setItemI18n] = useState<DishItemI18nShape>(() => buildInitialItemI18n());
    /** 与 RBP2 menu-dish-detail 一致：用户曾编辑过名称2 后不再随名称1 同步 */
    const [editDishName2, setEditDishName2] = useState(false);
    /** 用户改过外卖价后不再随堂食价同步 */
    const [takeoutUserEdited, setTakeoutUserEdited] = useState(false);
    const [dishType, setDishType] = useState('0');
    const [taxGroup, setTaxGroup] = useState('-1');
    const [printGroup, setPrintGroup] = useState('-1');
    const [mnemonic, setMnemonic] = useState('');
    const [dineIn, setDineIn] = useState('');
    const [takeout, setTakeout] = useState('');
    const [limitPerson, setLimitPerson] = useState('');
    const [limitTable, setLimitTable] = useState('');
    /** 计时菜品（item_type=7）：最低时间（分钟），非负整数 */
    const [minimumTime, setMinimumTime] = useState('');
    const [priceAfterLimit, setPriceAfterLimit] = useState('');
    const [condimentGroup, setCondimentGroup] = useState('-1');
    const [specialAttributes, setSpecialAttributes] = useState('0');
    const [discountOn, setDiscountOn] = useState(true);
    const [reportMain, setReportMain] = useState('-1');
    const [reportSub, setReportSub] = useState('-1');
    const [unit1, setUnit1] = useState('');
    const [portionNote, setPortionNote] = useState('');
    const [remarks, setRemarks] = useState('');
    const [allergenKeys, setAllergenKeys] = useState<string[]>([]);
    const [ingredientKeys, setIngredientKeys] = useState<string[]>([]);
    const [ignoreKwa, setIgnoreKwa] = useState(true);
    const [continueAfterExceed, setContinueAfterExceed] = useState(true);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [courseGroupDialogOpen, setCourseGroupDialogOpen] = useState(false);
    /** 点击「编辑套餐菜品」且需先保存时的按钮 loading（直接打开弹窗不设 true） */
    const [courseGroupEntryLoading, setCourseGroupEntryLoading] = useState(false);
    /** 首次 post_dishes 后列表可能尚未同步，`getResolvedCourseGroupDishItemId` 为 0 时用本次 insertId 打开套餐弹窗 */
    const [pendingCourseGroupItemId, setPendingCourseGroupItemId] = useState<number | null>(null);
    /** 云端套餐：create 后尚未挂到编辑行时的云端菜品 id */
    const [pendingCloudDishId, setPendingCloudDishId] = useState<string | null>(null);
    /** 云端套餐组草稿（套餐弹窗保存后 / 详情回填）；菜品保存时一并提交 */
    const [pendingCloudCourseGroups, setPendingCloudCourseGroups] = useState<CloudDishLibraryCourseGroup[] | null>(
        null,
    );
    /** 菜品静态图展示/回填用（与 `post_dishes`/`patch_dishes` 解耦，不落保存菜品请求体） */
    const [dishImageFileName, setDishImageFileName] = useState('');

    /** 批量模式：仅对已修改字段打补丁；文本类用 Set 记录 touched */
    const [batchTextTouched, setBatchTextTouched] = useState<Set<string>>(() => new Set());
    const [batchClassIdSel, setBatchClassIdSel] = useState<string>(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
    const [batchIgnoreKwaSel, setBatchIgnoreKwaSel] = useState<string>(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
    const [batchLimitEnableSel, setBatchLimitEnableSel] = useState<string>(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
    const [batchAllergenTouched, setBatchAllergenTouched] = useState(false);
    const [batchIngredientTouched, setBatchIngredientTouched] = useState(false);
    /**
     * `handleSave({ skipCloseOnSuccessOpenCourseGroup: true })` 成功后列表/promote 会触发回填 effect；
     * 此时只应更新 `pendingCourseGroupItemId` / 打开套餐弹窗，不要再用服务端行覆盖当前表单。
     */
    const suppressNextAddDishFormHydrateRef = useRef(false);
    /**
     * 记录上一帧「菜品类型」字符串：仅在「非计时」时清空最低时间。
     * 避免与编辑回填竞态——回填 effect 把 `dishType` 设为 7、`minimumTime` 设为 `5` 的同一轮里，
     * 本 effect 若仍读到旧的 `dishType===0`，会误执行 `setMinimumTime('')` 覆盖回填值。
     */
    const prevDishTypeForMinTimeRef = useRef<string | undefined>(undefined);

    const secondMenuDishCount = state.getCurrentSecondMenu()?.menu_item?.length ?? 0;
    const createSubDraftDishCount =
        state.createMenuSubcategoryDetailTempId != null
            ? state.createMenuSubcategoryDetailDishesByTempId[state.createMenuSubcategoryDetailTempId]?.length ?? 0
            : 0;

    /**
     * 仅当「弹窗开关 / 编辑目标」变化时变化。MobX observer 会因其它字段重渲染，但用该 key 可避免
     * `useEffect` 在 `isCreateMenuSubcategoryAddDishDialogOpen` 一直为 true 时被误触发（Strict Mode 仍会双重执行 mount）。
     */
    const addDishFormHydrateKey = useMemo(
        () =>
            isStandaloneReadonly
                ? `standalone|${standaloneHydrateKey}`
                : !state.isCreateMenuSubcategoryAddDishDialogOpen
                  ? 'closed'
                  : [
                        state.menuLibraryAddDishDialogMode,
                        state.batchDishSettingsRowKeys.join(','),
                        state.createMenuSubcategoryAddDishEditRowKey ?? '',
                        String(state.menuLibraryDetailAddDishEditItemId ?? ''),
                        state.menuLibraryDetailAddDishEditCloudDishId ?? '',
                        String(secondMenuDishCount),
                        String(createSubDraftDishCount),
                    ].join('|'),
        [
            isStandaloneReadonly,
            standaloneHydrateKey,
            state.isCreateMenuSubcategoryAddDishDialogOpen,
            state.menuLibraryAddDishDialogMode,
            state.batchDishSettingsRowKeys,
            state.createMenuSubcategoryAddDishEditRowKey,
            state.menuLibraryDetailAddDishEditItemId,
            state.menuLibraryDetailAddDishEditCloudDishId,
            secondMenuDishCount,
            createSubDraftDishCount,
        ],
    );

    /** standalone 编辑目标引用稳定，避免 `useCallback` 依赖每帧变化 */
    const standaloneEditTargetMemo = useMemo(() => {
        if (standaloneReadonlyDish == null) {
            return null;
        }
        return { dish: standaloneReadonlyDish, index: 0 } as const;
    }, [standaloneReadonlyDish]);

    /** 编辑目标：菜单库从 state 解析；standalone 时用传入行 */
    const addDishEditTargetResolved: { dish: MenuDish; index: number } | null =
        standaloneEditTargetMemo != null ? standaloneEditTargetMemo : state.getAddDishEditTarget();

    const markBatchTextTouched = useCallback((key: string) => {
        setBatchTextTouched((prev) => new Set(prev).add(key));
    }, []);

    /** 当前表单对应的二级菜单 id（新建子类草稿 / 详情页） */
    // const resolveAddDishFormSluId = useCallback((): number | null => {
    //     const tid = state.createMenuSubcategoryDetailTempId;
    //     const row = tid ? state.createMenuSubcategoryDrafts.find((r) => r.tempId === tid) : undefined;
    //     return row?.dmi_slu_id ?? state.getCurrentSecondMenu()?.dmi_slu_id ?? null;
    // }, [state]);

    const pendingDishImageFile = state.menuLibraryAddDishPendingDishImageFile;
    const [stagedDishImageObjectUrl, setStagedDishImageObjectUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!pendingDishImageFile) {
            setStagedDishImageObjectUrl(null);
            return;
        }
        const url = URL.createObjectURL(pendingDishImageFile);
        setStagedDishImageObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pendingDishImageFile]);

    const dishImageDisplayUrl = useMemo(
        () => stagedDishImageObjectUrl ?? buildRestaurantStaticImageDisplayUrl(dishImageFileName || undefined),
        [stagedDishImageObjectUrl, dishImageFileName],
    );

    const onStagedDishImageFile = useCallback((file: File) => {
        state.setMenuLibraryAddDishPendingDishImageFile(file);
    }, [state]);

    const onDeferredDishImageRemove = useCallback(() => {
        if (state.menuLibraryAddDishPendingDishImageFile) {
            state.setMenuLibraryAddDishPendingDishImageFile(null);
            return;
        }
        state.markMenuLibraryAddDishPendingRemoveServerDishImage();
        setDishImageFileName('');
    }, [state]);

    const resetBatchFormFields = useCallback(() => {
        setDishType(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setTaxGroup(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setPrintGroup(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setMnemonic('');
        setDineIn('');
        setTakeout('');
        setLimitPerson('');
        setLimitTable('');
        setMinimumTime('');
        setPriceAfterLimit('');
        setCondimentGroup(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setSpecialAttributes(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setDiscountOn(true);
        setReportMain(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setReportSub(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setUnit1(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setName1('');
        setName2('');
        setItemI18n(buildInitialItemI18n());
        setEditDishName2(false);
        setTakeoutUserEdited(false);
        setPortionNote('');
        setRemarks('');
        setAllergenKeys([]);
        setIngredientKeys([]);
        setBatchClassIdSel(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setBatchIgnoreKwaSel(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setBatchLimitEnableSel(MENU_LIB_ADD_DISH_BATCH_NO_CHANGE);
        setBatchTextTouched(new Set());
        setBatchAllergenTouched(false);
        setBatchIngredientTouched(false);
        setAddDishDisplayLang('unspecified');
        setDishImageFileName('');
        state.resetMenuLibraryAddDishImageStaging('');
    }, [state]);

    const getI18nField = useCallback(
        (key: I18nDishTextKey) => {
            const lk = rbpLangKeyFromMenuLib(addDishDisplayLang);
            if (isBatchMode) {
                const sub = getI18nMap(key);
                const touchKey =
                    lk === 'default'
                        ? key === 'item_name1'
                            ? 'name1'
                            : key === 'item_name2'
                                ? 'name2'
                                : key === 'item_description'
                                    ? 'remarks'
                                    : 'portion'
                        : `i18n:${lk}:${sub}`;
                if (!batchTextTouched.has(touchKey)) {
                    return '';
                }
            }
            if (lk === 'default') {
                if (key === 'item_name1') {
                    return name1;
                }
                if (key === 'item_name2') {
                    return name2;
                }
                if (key === 'item_description') {
                    return remarks;
                }
                return portionNote;
            }
            const sub = getI18nMap(key);
            return String(_.get(itemI18n, [lk, sub], '') ?? '');
        },
        [addDishDisplayLang, name1, name2, remarks, portionNote, itemI18n, isBatchMode, batchTextTouched],
    );

    const setI18nField = useCallback(
        (key: I18nDishTextKey, value: string) => {
            const lk = rbpLangKeyFromMenuLib(addDishDisplayLang);
            const sub = getI18nMap(key);
            if (state.menuLibraryAddDishDialogMode === 'batch') {
                const touchKey =
                    lk === 'default'
                        ? key === 'item_name1'
                            ? 'name1'
                            : key === 'item_name2'
                                ? 'name2'
                                : key === 'item_description'
                                    ? 'remarks'
                                    : 'portion'
                        : `i18n:${lk}:${sub}`;
                markBatchTextTouched(touchKey);
            }
            if (lk === 'default') {
                if (key === 'item_name1') {
                    setName1(value);
                    if (!editDishName2) {
                        setName2(value);
                    }
                } else if (key === 'item_name2') {
                    setName2(value);
                    setEditDishName2(!!value.trim());
                } else if (key === 'item_description') {
                    setRemarks(value);
                } else {
                    setPortionNote(value);
                }
                setItemI18n((prev) => {
                    const next = _.cloneDeep(prev);
                    _.set(next, ['default', sub], value);
                    return next;
                });
            } else {
                setItemI18n((prev) => {
                    const next = _.cloneDeep(prev);
                    _.set(next, [lk, sub], value);
                    return next;
                });
            }
        },
        [addDishDisplayLang, editDishName2, markBatchTextTouched, state.menuLibraryAddDishDialogMode],
    );

    /**
     * 打开「新增/编辑菜品」弹窗时：新增则重置表单；编辑则按当前行 `MenuDish` 回填（含 `item_i18n`）
     * 依赖仅 `addDishFormHydrateKey`：MobX observer 会因其它 observable 重渲染，但不会重复执行本 effect。
     */
    useEffect(() => {
        if (isStandaloneReadonly && standaloneReadonlyDish != null) {
            if (suppressNextAddDishFormHydrateRef.current) {
                suppressNextAddDishFormHydrateRef.current = false;
                return;
            }
            const dish = standaloneReadonlyDish;
            const merged = buildItemI18nMergedFromDish(dish);
            const { name1: n1, name2: n2 } = resolveDishFormNamesFromHydrated(merged, dish);
            setName1(n1);
            setName2(n2);
            setItemI18n(merged);
            setEditDishName2(n2.trim() !== '' && n2.trim() !== n1.trim());
            setTakeoutUserEdited(
                dish.takeout_price_1 != null &&
                    dish.price_1 != null &&
                    Number(dish.takeout_price_1) !== Number(dish.price_1),
            );
            setDishType(String(dish.item_type ?? 0));
            setTaxGroup(String(dish.tax_group ?? -1));
            setPrintGroup(String(dish.print_class ?? -1));
            setMnemonic((dish.nlu ?? '').trim());
            setDineIn(dish.price_1 != null ? String(dish.price_1) : '');
            setTakeout(dish.takeout_price_1 != null ? String(dish.takeout_price_1) : '');
            const lp = dish.rule_lnr_max_num;
            setLimitPerson(lp != null && lp !== 9999 ? String(lp) : '');
            const lt = dish.rule_table_max_num;
            setLimitTable(lt != null && lt !== 9999 ? String(lt) : '');
            const mt = dish.minimum_time;
            setMinimumTime(mt != null && mt > 0 ? String(mt) : '');
            const pal = dish.rule_lnr_limit_price;
            setPriceAfterLimit(pal != null && pal !== 0 ? String(pal) : '');
            setCondimentGroup(String(dish.required_condiment ?? -1));
            setSpecialAttributes(String(dish.special_attributes ?? 0));
            setDiscountOn((dish.class_id ?? 3) === 6);
            setReportMain(String(dish.major_group ?? -1));
            setReportSub(String(dish.family_group ?? -1));
            setUnit1((dish.unit_1 ?? '').trim());
            setRemarks(String(_.get(merged, ['default', 'remark'], dish.item_description ?? '')));
            setPortionNote(String(_.get(merged, ['default', 'extra_remark'], dish.ext_dish_info ?? '')));
            setAllergenKeys(parseDetailMultiKeysFromDish(dish.allergen));
            setIngredientKeys(parseDetailMultiKeysFromDish(dish.other_detail_info));
            setIgnoreKwa(Number(dish.rules_ignore_the_display) === 1);
            setContinueAfterExceed(Number(dish.rule_lnr_limit_enable) === 1);
            setAddDishDisplayLang('unspecified');
            return;
        }
        if (!state.isCreateMenuSubcategoryAddDishDialogOpen) {
            return;
        }
        if (suppressNextAddDishFormHydrateRef.current) {
            suppressNextAddDishFormHydrateRef.current = false;
            return;
        }
        if (state.menuLibraryAddDishDialogMode === 'batch') {
            resetBatchFormFields();
            return;
        }
        const editTarget = state.getAddDishEditTarget();
        if (editTarget != null) {
            const dish = editTarget.dish;
            const merged = buildItemI18nMergedFromDish(dish);
            const { name1: n1, name2: n2 } = resolveDishFormNamesFromHydrated(merged, dish);
            setName1(n1);
            setName2(n2);
            setItemI18n(merged);
            setEditDishName2(n2.trim() !== '' && n2.trim() !== n1.trim());
            setTakeoutUserEdited(
                dish.takeout_price_1 != null &&
                dish.price_1 != null &&
                Number(dish.takeout_price_1) !== Number(dish.price_1),
            );
            setDishType(String(dish.item_type ?? 0));
            setTaxGroup(String(dish.tax_group ?? -1));
            setPrintGroup(String(dish.print_class ?? -1));
            setMnemonic((dish.nlu ?? '').trim());
            setDineIn(dish.price_1 != null ? String(dish.price_1) : '');
            setTakeout(dish.takeout_price_1 != null ? String(dish.takeout_price_1) : '');
            const lp = dish.rule_lnr_max_num;
            setLimitPerson(lp != null && lp !== 9999 ? String(lp) : '');
            const lt = dish.rule_table_max_num;
            setLimitTable(lt != null && lt !== 9999 ? String(lt) : '');
            const mt = dish.minimum_time;
            setMinimumTime(mt != null && mt > 0 ? String(mt) : '');
            const pal = dish.rule_lnr_limit_price;
            setPriceAfterLimit(pal != null && pal !== 0 ? String(pal) : '');
            setCondimentGroup(String(dish.required_condiment ?? -1));
            setSpecialAttributes(String(dish.special_attributes ?? 0));
            setDiscountOn((dish.class_id ?? 3) === 6);
            setReportMain(String(dish.major_group ?? -1));
            setReportSub(String(dish.family_group ?? -1));
            setUnit1((dish.unit_1 ?? '').trim());
            setRemarks(String(_.get(merged, ['default', 'remark'], dish.item_description ?? '')));
            setPortionNote(String(_.get(merged, ['default', 'extra_remark'], dish.ext_dish_info ?? '')));
            setAllergenKeys(parseDetailMultiKeysFromDish(dish.allergen));
            setIngredientKeys(parseDetailMultiKeysFromDish(dish.other_detail_info));
            setIgnoreKwa(Number(dish.rules_ignore_the_display) === 1);
            setContinueAfterExceed(Number(dish.rule_lnr_limit_enable) === 1);
            setAddDishDisplayLang('unspecified');
            setDishImageFileName(String(dish.dish_image ?? '').trim());
            const cg = (dish as unknown as CloudDishLibraryMenuDetailDish).course_groups;
            setPendingCloudCourseGroups(Array.isArray(cg) ? cg : null);
            return;
        }
        /** promote 后 `menuLibraryDetailAddDishEditItemId` 已更新但列表尚未含新菜时，勿走「新增默认」以免 `setDishType('0')` */
        const hasPendingEditContext =
            state.menuLibraryDetailAddDishEditItemId != null ||
            state.menuLibraryDetailAddDishEditCloudDishId != null ||
            state.createMenuSubcategoryAddDishEditRowKey != null;
        /** 列表未挂上该行时 state 里已有 item_id（见 getResolvedCourseGroupDishItemId）或刚写入 pending，勿清空成普通菜 */
        const resolvedDishItemId = state.getResolvedCourseGroupDishItemId();
        const hasKnownItemId =
            resolvedDishItemId > 0 || (pendingCourseGroupItemId != null && pendingCourseGroupItemId > 0);
        if (hasPendingEditContext || hasKnownItemId) {
            return;
        }
        setName1(DEFAULT_NEW_DISH_NAME);
        setName2(DEFAULT_NEW_DISH_NAME);
        setItemI18n(buildInitialItemI18n());
        setEditDishName2(false);
        setTakeoutUserEdited(false);
        setDishType('0');
        setTaxGroup('-1');
        setPrintGroup('-1');
        setMnemonic('');
        setDineIn('');
        setTakeout('');
        setLimitPerson('');
        setLimitTable('');
        setMinimumTime('');
        setPriceAfterLimit('');
        setCondimentGroup('-1');
        setSpecialAttributes('0');
        setDiscountOn(true);
        setReportMain('-1');
        setReportSub('-1');
        setUnit1('');
        setPortionNote('');
        setRemarks('');
        setAllergenKeys([]);
        setIngredientKeys([]);
        setIgnoreKwa(true);
        setContinueAfterExceed(true);
        setAddDishDisplayLang('unspecified');
        setDishImageFileName('');
        setPendingCloudDishId(null);
        setPendingCloudCourseGroups(null);
        state.resetMenuLibraryAddDishImageStaging('');
    }, [
        addDishFormHydrateKey,
        pendingCourseGroupItemId,
        resetBatchFormFields,
        state.menuLibraryAddDishDialogMode,
        isStandaloneReadonly,
        standaloneReadonlyDish,
        state,
    ]);

    /** 切换为非计时菜品时清空最低时间，避免提交脏数据（首帧不执行，见 `prevDishTypeForMinTimeRef`） */
    useEffect(() => {
        if (dishType === MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            prevDishTypeForMinTimeRef.current = dishType;
            return;
        }
        const prev = prevDishTypeForMinTimeRef.current;
        prevDishTypeForMinTimeRef.current = dishType;
        if (prev === undefined) {
            return;
        }
        if (Number(dishType) !== TIMED_DISH_ITEM_TYPE) {
            setMinimumTime('');
        }
    }, [dishType]);

    const currency = state.getModuleCurrencyName();

    const allergenOptions: Option[] = useMemo(
        () =>
            ALLERGEN.map((item) => ({
                value: item.key,
                label: i18n.t(item.text),
                icon: <img style={{ width: 20, height: 20 }} alt="" src={item.icon} />,
            })),
        [],
    );

    const ingredientOptions: Option[] = useMemo(
        () =>
            INGREDIENTS.map((item) => ({
                value: item.key,
                label: i18n.t(item.text),
                icon: <img style={{ width: 20, height: 20 }} alt="" src={item.icon} />,
            })),
        [],
    );

    const allergenValue = useMemo(() => {
        const keys = isBatchMode && !batchAllergenTouched ? [] : allergenKeys;
        return allergenOptions.filter((o) => keys.includes(o.value));
    }, [allergenOptions, allergenKeys, isBatchMode, batchAllergenTouched]);

    const ingredientValue = useMemo(() => {
        const keys = isBatchMode && !batchIngredientTouched ? [] : ingredientKeys;
        return ingredientOptions.filter((o) => keys.includes(o.value));
    }, [ingredientOptions, ingredientKeys, isBatchMode, batchIngredientTouched]);

    useEffect(() => {
        if (!dialogOpenForEffects) {
            return;
        }
        let cancelled = false;
        /** 云端菜品弹窗：仅 check-permission 通过才拉 §5.5；校验中保持 loading 等待标志位 */
        const isCloudRefContext =
            !isStandaloneReadonly && (state.isCloudMenuDetail || state.createSource === 'cloud');
        const tunnelPermissionOk = state.menuLibraryTunnelPermissionOk;
        (async () => {
            setRefLoading(true);
            setCondimentGroupsRaw([]);
            /** 调味品组下拉的「无」选项，拉取失败时用于移除旧餐厅选项 */
            const noneCond = {
                value: '-1',
                label: i18n.t('print_setting_page_none0'),
            };
            /** 仅占位「无 / 未指定」，不发隧道下拉请求 */
            const applyEmptyRefPlaceholders = () => {
                setTaxItems([{ value: '-1', label: i18n.t(NOT_SPECIFIED) }]);
                setCondimentItems([noneCond]);
                setCondimentGroupsRaw([]);
                setPrintItems([{ value: '-1', label: i18n.t('print_setting_page_none0') }]);
                setUnitItems([{ value: '', label: i18n.t(NOT_SPECIFIED) }]);
                setMajorItems([{ value: '-1', label: i18n.t('print_setting_page_none0') }]);
                setFamilyItems([{ value: '-1', label: i18n.t('print_setting_page_none0') }]);
            };
            if (isCloudRefContext && tunnelPermissionOk === null) {
                /** 仍在 check-permission：保持 refLoading，待标志变化后本 effect 重跑 */
                return;
            }
            if (isCloudRefContext && tunnelPermissionOk === false) {
                if (!cancelled) {
                    applyEmptyRefPlaceholders();
                    setRefLoading(false);
                }
                return;
            }
            try {
                const [taxRaw, condRaw, printRaw, unitsRaw, majorRaw, familyRaw] = await Promise.all([
                    apiManage_rbp.get_tax_group(rbpId),
                    apiManage_rbp.get_condiments_group({ with_group: true }, rbpId),
                    apiManage_rbp.getPrinterClass(rbpId),
                    apiManage_rbp.getPriceSpecification(rbpId),
                    apiManage_rbp.getDishReportLabel(rbpId),
                    apiManage_rbp.getDishPostLabel(rbpId),
                ]);
                if (cancelled) {
                    return;
                }
                const taxOpts: SelectOption[] = [
                    { value: '-1', label: i18n.t(NOT_SPECIFIED) },
                    ...(Array.isArray(taxRaw) ? taxRaw : []).map((item: { tax_group_id?: number; tax_group_name?: string }) => ({
                        value: String(item.tax_group_id ?? -1),
                        label: item.tax_group_name || '',
                    })),
                ];
                const condOpts: SelectOption[] = [
                    noneCond,
                    ...(Array.isArray(condRaw) ? condRaw : []).map(
                        (item: { scomdiment_groups_id?: number; comdiment_groups_name?: string }) => ({
                            value: String(item.scomdiment_groups_id ?? -1),
                            label: item.comdiment_groups_name || '',
                        }),
                    ),
                ];
                const printOpts: SelectOption[] = [
                    { value: '-1', label: i18n.t('print_setting_page_none0') },
                    ...(Array.isArray(printRaw) ? printRaw : []).map(
                        (item: { print_class_id?: number; print_class_name?: string }) => ({
                            value: String(item.print_class_id ?? -1),
                            label: item.print_class_name || '',
                        }),
                    ),
                ];
                const unitOpts: SelectOption[] = [
                    { value: '', label: i18n.t(NOT_SPECIFIED) },
                    ...(Array.isArray(unitsRaw) ? unitsRaw : [])
                        .map((u: { unit_name?: string }) => u.unit_name)
                        .filter((name): name is string => Boolean(name))
                        .map((name) => ({ value: name, label: name })),
                ];
                const majorOpts: SelectOption[] = [
                    { value: '-1', label: i18n.t('print_setting_page_none0') },
                    ...(Array.isArray(majorRaw) ? majorRaw : []).map(
                        (item: { major_group_id?: number; major_group_name?: string }) => ({
                            value: String(item.major_group_id ?? -1),
                            label: item.major_group_name || '',
                        }),
                    ),
                ];
                const familyOpts: SelectOption[] = [
                    { value: '-1', label: i18n.t('print_setting_page_none0') },
                    ...(Array.isArray(familyRaw) ? familyRaw : []).map(
                        (item: { family_group_id?: number; family_group_name?: string }) => ({
                            value: String(item.family_group_id ?? -1),
                            label: item.family_group_name || '',
                        }),
                    ),
                ];
                setTaxItems(taxOpts);
                setCondimentItems(condOpts);
                setCondimentGroupsRaw(getCondimentGroupsRawAfterRefFetch({ ok: true, data: condRaw }));
                setPrintItems(printOpts);
                setUnitItems(unitOpts);
                setMajorItems(majorOpts);
                setFamilyItems(familyOpts);
            } catch (e) {
                if (!cancelled) {
                    setCondimentGroupsRaw(getCondimentGroupsRawAfterRefFetch({ ok: false, data: undefined }));
                    setCondimentItems([noneCond]);
                    setCondimentGroup('-1');
                    console.error('menu-library-add-dish ref data', e);
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                }
            } finally {
                if (!cancelled) {
                    setRefLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        rbpId,
        dialogOpenForEffects,
        isStandaloneReadonly,
        state.isCloudMenuDetail,
        state.createSource,
        state.menuLibraryTunnelPermissionOk,
    ]);

    const ncSelectOption = useMemo(
        () => ({ value: MENU_LIB_ADD_DISH_BATCH_NO_CHANGE, label: batchNoChangeLabel }),
        [batchNoChangeLabel],
    );

    const dishTypeSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...DISH_TYPE_ITEMS] : DISH_TYPE_ITEMS),
        [isBatchMode, ncSelectOption],
    );
    const taxSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...taxItems] : taxItems),
        [isBatchMode, ncSelectOption, taxItems],
    );
    const printSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...printItems] : printItems),
        [isBatchMode, ncSelectOption, printItems],
    );
    const condimentSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...condimentItems] : condimentItems),
        [isBatchMode, ncSelectOption, condimentItems],
    );
    const specialAttrSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...SPECIAL_ATTR_ITEMS] : SPECIAL_ATTR_ITEMS),
        [isBatchMode, ncSelectOption],
    );
    const majorSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...majorItems] : majorItems),
        [isBatchMode, ncSelectOption, majorItems],
    );
    const familySelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...familyItems] : familyItems),
        [isBatchMode, ncSelectOption, familyItems],
    );
    const unitSelectItems = useMemo(
        () => (isBatchMode ? [ncSelectOption, ...unitItems] : unitItems),
        [isBatchMode, ncSelectOption, unitItems],
    );
    const batchYesNoSelectItems: SelectOption[] = useMemo(
        () => [
            { value: MENU_LIB_ADD_DISH_BATCH_NO_CHANGE, label: batchNoChangeLabel },
            { value: '1', label: i18n.t('general_text_yes') },
            { value: '0', label: i18n.t('general_text_no') },
        ],
        [batchNoChangeLabel],
    );
    const batchDiscountSelectItems: SelectOption[] = useMemo(
        () => [
            { value: MENU_LIB_ADD_DISH_BATCH_NO_CHANGE, label: batchNoChangeLabel },
            { value: '6', label: i18n.t('menu_and_dishes_page_has_discount') },
            { value: '3', label: i18n.t('menu_and_dishes_page_has_not_discount') },
        ],
        [batchNoChangeLabel],
    );

    const topFieldRowSecond: Array<{
        key: string;
        labelKey: string;
        multilineLabel?: boolean;
        select?: boolean;
        node: React.ReactNode;
    }> = useMemo(
        () => [
            {
                key: 'type',
                labelKey: 'menu_library_create_dialog_subcategory_detail_dish_col_type',
                select: true,
                node: (
                    <CommonInput
                        inputType="SELECT"
                        hiddenLabel
                        fullWidth
                        size="small"
                        value={dishType}
                        items={dishTypeSelectItems}
                        onChange={(e) => setDishType(String((e.target as HTMLInputElement).value))}
                    />
                ),
            },
            {
                key: 'tax',
                labelKey: 'menu_library_add_dish_tax_group',
                select: true,
                multilineLabel: true,
                node: (
                    <CommonInput
                        inputType="SELECT"
                        hiddenLabel
                        fullWidth
                        size="small"
                        value={taxGroup}
                        items={taxSelectItems}
                        onChange={(e) => setTaxGroup(String((e.target as HTMLInputElement).value))}
                        disabled={refLoading}
                    />
                ),
            },
            {
                key: 'print',
                labelKey: 'menu_library_add_dish_print_station_label',
                select: true,
                multilineLabel: true,
                node: (
                    <CommonInput
                        inputType="SELECT"
                        hiddenLabel
                        fullWidth
                        size="small"
                        value={printGroup}
                        items={printSelectItems}
                        onChange={(e) => setPrintGroup(String((e.target as HTMLInputElement).value))}
                        disabled={refLoading}
                    />
                ),
            },
        ],
        [dishType, taxGroup, printGroup, dishTypeSelectItems, taxSelectItems, printSelectItems, refLoading],
    );

    /** 与改版前表单顺序一致：文本 / 下拉 / 开关穿插，便于三列折行阅读 */
    const mainGridFields: Array<
        | {
            kind: 'text';
            key: string;
            labelKey: string;
            value: string;
            setValue: (v: string) => void;
            placeholder?: string;
        }
        | {
            kind: 'nonNegDecimal';
            key: string;
            labelKey: string;
            value: string;
            setValue: (v: string) => void;
            placeholder?: string;
            /** 堂食价：失焦时若未单独改外卖则同步外卖 */
            syncTakeoutOnBlur?: boolean;
        }
        | {
            kind: 'nonNegInt';
            key: string;
            labelKey: string;
            value: string;
            setValue: (v: string) => void;
            placeholder?: string;
        }
        | {
            kind: 'select';
            key: string;
            labelKey: string;
            multilineLabel?: boolean;
            value: string;
            setValue: (v: string) => void;
            items: SelectOption[];
        }
    > = useMemo(() => {
        const b = (k: string) => {
            if (isBatchMode) {
                markBatchTextTouched(k);
            }
        };
        const numPh = (k: string) => (isBatchMode && !batchTextTouched.has(k) ? batchNoChangeLabel : undefined);
        return [
            {
                kind: 'text' as const,
                key: 'nlu',
                labelKey: 'menu_library_detail_col_nlu',
                value: isBatchMode && !batchTextTouched.has('nlu') ? '' : mnemonic,
                placeholder: isBatchMode && !batchTextTouched.has('nlu') ? batchNoChangeLabel : undefined,
                setValue: (v: string) => {
                    b('nlu');
                    setMnemonic(v);
                },
            },
            {
                kind: 'nonNegDecimal' as const,
                key: 'dine',
                labelKey: 'menu_library_detail_col_dine_in',
                value: isBatchMode && !batchTextTouched.has('dine') ? '' : dineIn,
                setValue: (v: string) => {
                    b('dine');
                    setDineIn(v);
                },
                placeholder: isBatchMode && !batchTextTouched.has('dine') ? batchNoChangeLabel : `${currency} `,
                syncTakeoutOnBlur: true,
            },
            {
                kind: 'nonNegDecimal' as const,
                key: 'take',
                labelKey: 'menu_library_create_dialog_subcategory_detail_dish_col_takeout',
                value: isBatchMode && !batchTextTouched.has('take') ? '' : takeout,
                setValue: (v: string) => {
                    b('take');
                    setTakeout(v);
                },
                placeholder: numPh('take'),
            },
            {
                kind: 'nonNegInt' as const,
                key: 'lp',
                labelKey: 'menu_library_add_dish_limit_per_person',
                value: isBatchMode && !batchTextTouched.has('lp') ? '' : limitPerson,
                setValue: (v: string) => {
                    b('lp');
                    setLimitPerson(v);
                },
                placeholder: numPh('lp'),
            },
            {
                kind: 'nonNegInt' as const,
                key: 'lt',
                labelKey: 'menu_library_add_dish_limit_per_table',
                value: isBatchMode && !batchTextTouched.has('lt') ? '' : limitTable,
                setValue: (v: string) => {
                    b('lt');
                    setLimitTable(v);
                },
                placeholder: numPh('lt'),
            },
            {
                kind: 'nonNegDecimal' as const,
                key: 'pal',
                labelKey: 'menu_library_add_dish_price_after_limit',
                value: isBatchMode && !batchTextTouched.has('pal') ? '' : priceAfterLimit,
                setValue: (v: string) => {
                    b('pal');
                    setPriceAfterLimit(v);
                },
                placeholder: numPh('pal'),
            },
            {
                kind: 'select' as const,
                key: 'cond',
                labelKey: 'menu_library_add_dish_condiment_group',
                value: condimentGroup,
                setValue: setCondimentGroup,
                items: condimentSelectItems,
            },
            {
                kind: 'select' as const,
                key: 'tag',
                labelKey: 'menu_library_add_dish_dish_tag',
                value: specialAttributes,
                setValue: setSpecialAttributes,
                items: specialAttrSelectItems,
            },
            {
                kind: 'select' as const,
                key: 'major',
                labelKey: 'menu_library_add_dish_report_main',
                multilineLabel: true,
                value: reportMain,
                setValue: setReportMain,
                items: majorSelectItems,
            },
            {
                kind: 'select' as const,
                key: 'family',
                labelKey: 'menu_library_add_dish_report_sub',
                multilineLabel: true,
                value: reportSub,
                setValue: setReportSub,
                items: familySelectItems,
            },
            {
                kind: 'select' as const,
                key: 'unit',
                labelKey: 'menu_library_add_dish_spec',
                value: unit1,
                setValue: setUnit1,
                items: unitSelectItems,
            },
            {
                kind: 'text' as const,
                key: 'portion',
                labelKey: 'menu_library_add_dish_portion',
                value: getI18nField('ext_dish_info'),
                placeholder: isBatchMode && !batchTextTouched.has('portion') ? batchNoChangeLabel : undefined,
                setValue: (v: string) => setI18nField('ext_dish_info', v),
            },
        ];
    }, [
        isBatchMode,
        batchNoChangeLabel,
        batchTextTouched,
        markBatchTextTouched,
        mnemonic,
        dineIn,
        takeout,
        limitPerson,
        limitTable,
        priceAfterLimit,
        condimentGroup,
        specialAttributes,
        reportMain,
        reportSub,
        unit1,
        currency,
        condimentSelectItems,
        specialAttrSelectItems,
        majorSelectItems,
        familySelectItems,
        unitSelectItems,
        getI18nField,
    ]);

    type SaveDishOptions = {
        /** 保存成功后不关闭「新增/编辑菜品」弹窗，并打开套餐组配置（仅用于：新增套餐菜先落库再编辑套餐组） */
        skipCloseOnSuccessOpenCourseGroup?: boolean;
    };

    const handleBatchSave = useCallback(async () => {
        if (isStandaloneReadonly) {
            return;
        }
        const targets = state.getBatchDishSettingsTargets();
        if (!targets.length) {
            GlobalNotification.error(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }
        const sluFallback =
            state.createMenuSubcategoryDrafts.find((r) => r.tempId === state.createMenuSubcategoryDetailTempId)
                ?.dmi_slu_id ?? state.getCurrentSecondMenu()?.dmi_slu_id;
        const sluId = targets[0].dish.slu_id ?? sluFallback;
        if (sluId == null) {
            GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
            return;
        }

        if (dishType === '7') {
            if (!batchTextTouched.has('minTime') || numOrDefault(minimumTime, 0) <= 0) {
                GlobalNotification.error(i18n.t('menu_and_dishes_page_min_time_tip'));
                return;
            }
            if (numOrDefault(dineIn, 0) <= 0) {
                GlobalNotification.error(i18n.t('menu_and_dishes_page_min_price_tip'));
                return;
            }
        }

        const rk = rbpLangKeyFromMenuLib(addDishDisplayLang);
        const touched = batchTextTouched;

        const hasNameCluster =
            touched.has('name1') ||
            touched.has('name2') ||
            touched.has('remarks') ||
            touched.has('portion') ||
            [...touched].some((k) => k.startsWith('i18n:'));

        if (touched.has('nlu')) {
            if (!BASE_CHECK_TYPE.dishNameAndNlu.regular.test(mnemonic.trim())) {
                GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular'));
                return;
            }
        }

        const buildNamePartForDish = (dish: MenuDish): Partial<PatchDishesParams> | null => {
            if (!hasNameCluster) {
                return {};
            }
            const merged = _.cloneDeep(buildItemI18nMergedFromDish(dish));
            if (touched.has('name1')) {
                _.set(merged, ['default', 'name1'], name1.trim());
            }
            if (touched.has('name2')) {
                _.set(merged, ['default', 'name2'], (name2.trim() || name1).trim());
            }
            if (touched.has('remarks')) {
                _.set(merged, ['default', 'remark'], remarks.trim());
            }
            if (touched.has('portion')) {
                _.set(merged, ['default', 'extra_remark'], portionNote.trim());
            }
            for (const t of touched) {
                if (!t.startsWith('i18n:')) {
                    continue;
                }
                const segs = t.split(':');
                if (segs.length !== 3) {
                    continue;
                }
                const lang = segs[1] as keyof DishItemI18nShape;
                const sub = segs[2];
                if (!['name1', 'name2', 'remark', 'extra_remark'].includes(sub)) {
                    continue;
                }
                const v = String(_.get(itemI18n, [lang, sub], '') ?? '');
                _.set(merged, [lang, sub], v);
            }
            const im = merged as Record<
                string,
                { name1?: string; name2?: string; remark?: string; extra_remark?: string } | undefined
            >;
            const dName1 = String(_.get(merged, ['default', 'name1'], '') ?? '').trim();
            const item_name1_api =
                dName1 ||
                (im.IT?.name1 || '').trim() ||
                (rk !== 'default' ? (im[rk]?.name1 || '').trim() : '') ||
                '';
            if (!item_name1_api) {
                GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_reuqired'));
                return null;
            }
            const name1ForItaly =
                rk === 'default' ? dName1 : String(_.get(merged, [rk, 'name1'], '') ?? '');
            if (isItaly && !BASE_CHECK_TYPE.dishNameAndNlu2.regular.test(name1ForItaly)) {
                GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular2'));
                return null;
            }
            const name2FromDefault = String(_.get(merged, ['default', 'name2'], '') ?? '').trim();
            const name2Final =
                name2FromDefault ||
                (im.IT?.name2 || '').trim() ||
                (rk !== 'default' ? (im[rk]?.name2 || '').trim() : '') ||
                item_name1_api;
            const name2ForItaly =
                rk === 'default'
                    ? name2FromDefault || dName1
                    : String(_.get(merged, [rk, 'name2'], '') || _.get(merged, [rk, 'name1'], '') || '');
            if (isItaly && !BASE_CHECK_TYPE.dishNameAndNlu.regular.test(String(name2ForItaly))) {
                GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular'));
                return null;
            }
            const defaultRemark = String(_.get(merged, ['default', 'remark'], '') ?? '').trim();
            const item_description_api =
                defaultRemark ||
                (im.IT?.remark || '').trim() ||
                (rk !== 'default' ? (im[rk]?.remark || '').trim() : '') ||
                null;
            const ext_dish_info_api =
                String(_.get(merged, ['default', 'extra_remark'], '') ?? '').trim() ||
                (im.IT?.extra_remark || '').trim() ||
                (rk !== 'default' ? (im[rk]?.extra_remark || '').trim() : '') ||
                '';
            return {
                item_name1: item_name1_api,
                item_name2: name2Final,
                item_description: item_description_api,
                ext_dish_info: ext_dish_info_api,
                item_i18n: JSON.stringify(merged),
            };
        };

        const shared: Partial<PatchDishesParams> = {};
        if (dishType !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.item_type = Number(dishType);
        }
        if (taxGroup !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.tax_group = parseSelectInt(taxGroup, -1);
        }
        if (printGroup !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.print_class = parseSelectInt(printGroup, -1);
        }
        if (condimentGroup !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.required_condiment = parseSelectInt(condimentGroup, -1);
        }
        if (specialAttributes !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.special_attributes = parseSelectInt(specialAttributes, 0);
        }
        if (reportMain !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.major_group = parseSelectInt(reportMain, -1);
        }
        if (reportSub !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.family_group = parseSelectInt(reportSub, -1);
        }
        if (unit1 !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.unit_1 = unit1.trim() || null;
        }
        if (touched.has('nlu')) {
            shared.nlu = mnemonic.trim();
        }
        if (touched.has('dine')) {
            shared.price_1 = numOrDefault(dineIn, 0);
        }
        if (touched.has('take')) {
            shared.takeout_price_1 = numOrDefault(takeout, 0);
        }
        if (touched.has('lp')) {
            shared.rule_lnr_max_num = numOrDefault(limitPerson, 9999);
        }
        if (touched.has('lt')) {
            shared.rule_table_max_num = numOrDefault(limitTable, 9999);
        }
        if (touched.has('pal')) {
            shared.rule_lnr_limit_price = numOrDefault(priceAfterLimit, 0);
        }
        if (touched.has('minTime')) {
            shared.minimum_time = numOrDefault(minimumTime, 0);
        }
        if (batchClassIdSel !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.class_id = Number(batchClassIdSel);
        }
        if (batchIgnoreKwaSel !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.rules_ignore_the_display = Number(batchIgnoreKwaSel);
        }
        if (batchLimitEnableSel !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            shared.rule_lnr_limit_enable = Number(batchLimitEnableSel);
        }
        if (batchAllergenTouched) {
            const allergenStr = allergenKeys.filter((k) => ALLERGEN.some((a) => a.key === k)).join('*');
            shared.allergen = allergenStr;
        }
        if (batchIngredientTouched) {
            const otherStr = ingredientKeys.filter((k) => INGREDIENTS.some((a) => a.key === k)).join('*');
            shared.other_detail_info = otherStr;
        }

        const nameProbe = buildNamePartForDish(targets[0].dish);
        if (nameProbe === null) {
            return;
        }
        const probeKeys = Object.keys({ ...shared, ...nameProbe }).filter((k) => k !== 'item_id' && k !== 'slu_id');
        if (probeKeys.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_batch_no_field_changed'));
            return;
        }

        const payloads: PatchDishesParams[] = [];
        for (const { dish } of targets) {
            const itemId = dish.item_id;
            if (itemId == null) {
                return;
            }
            const namePart = buildNamePartForDish(dish);
            if (namePart === null) {
                return;
            }
            payloads.push({
                slu_id: dish.slu_id ?? sluId,
                item_id: itemId,
                ...shared,
                ...namePart,
            });
        }
        if (!payloads.length) {
            return;
        }

        try {
            setSaveLoading(true);
            setLoading(true);
            const patchRes = await apiManage_rbp.patch_dishes(payloads, true, rbpId);
            if (_.get(patchRes, 'error')) {
                throw new Error(i18n.t('global_text_operate_failure'));
            }
            targets.forEach(({ dish, index }, i) => {
                const p = payloads[i];
                const updated: MenuDish = {
                    ...dish,
                    ...(p as unknown as MenuDish),
                    item_id: dish.item_id,
                    slu_id: dish.slu_id ?? p.slu_id,
                    allergen: batchAllergenTouched ? allergenKeys : dish.allergen,
                    other_detail_info: batchIngredientTouched ? ingredientKeys : dish.other_detail_info,
                };
                state.replaceCreateMenuSubcategoryDishAtIndex(index, updated);
            });
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            onClose();
        } catch (e) {
            GlobalNotification.error(typeof e === 'string' ? e : i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            setSaveLoading(false);
        }
    }, [
        addDishDisplayLang,
        allergenKeys,
        batchAllergenTouched,
        batchClassIdSel,
        batchIgnoreKwaSel,
        batchIngredientTouched,
        batchLimitEnableSel,
        batchTextTouched,
        condimentGroup,
        dishType,
        dineIn,
        ingredientKeys,
        isItaly,
        itemI18n,
        limitPerson,
        limitTable,
        mnemonic,
        name1,
        name2,
        onClose,
        portionNote,
        printGroup,
        remarks,
        reportMain,
        reportSub,
        rbpId,
        specialAttributes,
        state,
        takeout,
        taxGroup,
        unit1,
        priceAfterLimit,
        minimumTime,
        isStandaloneReadonly,
    ]);

    /**
     * 云端详情批量设置：只提交已改字段，走 `POST .../menus/dishes/batch-update`。
     * 不传本地专有的 `rules_ignore_the_display` / `rule_lnr_limit_enable`（OAS 未列）。
     */
    const handleCloudBatchSave = useCallback(async () => {
        if (isStandaloneReadonly) {
            return;
        }
        const targets = state.getBatchDishSettingsTargets();
        if (!targets.length) {
            GlobalNotification.error(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }
        const cloudMenuId = state.detailCloudMenuId ?? state.createDialogPersistedCloudMenuId;
        if (!cloudMenuId) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return;
        }
        const cloudDishIds = targets
            .map(({ dish }) => getCloudDishIdFromMenuDish(dish))
            .filter((id): id is string => !!id);
        if (cloudDishIds.length === 0) {
            GlobalNotification.error(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }

        if (dishType === '7') {
            if (!batchTextTouched.has('minTime') || numOrDefault(minimumTime, 0) <= 0) {
                GlobalNotification.error(i18n.t('menu_and_dishes_page_min_time_tip'));
                return;
            }
            if (numOrDefault(dineIn, 0) <= 0) {
                GlobalNotification.error(i18n.t('menu_and_dishes_page_min_price_tip'));
                return;
            }
        }

        const rk = rbpLangKeyFromMenuLib(addDishDisplayLang);
        const touched = batchTextTouched;
        const hasNameCluster =
            touched.has('name1') ||
            touched.has('name2') ||
            touched.has('remarks') ||
            touched.has('portion') ||
            [...touched].some((k) => k.startsWith('i18n:'));

        if (touched.has('nlu')) {
            if (!BASE_CHECK_TYPE.dishNameAndNlu.regular.test(mnemonic.trim())) {
                GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular'));
                return;
            }
        }

        const patch: CloudDishLibraryBatchUpdateDishPatch = {};
        if (dishType !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.item_type = Number(dishType);
        }
        if (taxGroup !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.tax_group = parseSelectInt(taxGroup, -1);
        }
        if (printGroup !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.print_class = parseSelectInt(printGroup, -1);
        }
        if (condimentGroup !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            const requiredCondimentId = parseSelectInt(condimentGroup, -1);
            patch.required_condiment = requiredCondimentId;
            const condimentGroupPayload = resolveCloudDishCondimentGroupForCreate(
                condimentGroupsRaw,
                requiredCondimentId,
            );
            if (condimentGroupPayload) {
                patch.condiment_group = condimentGroupPayload;
            }
        }
        if (specialAttributes !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.special_attributes = parseSelectInt(specialAttributes, 0);
        }
        if (reportMain !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.major_group = parseSelectInt(reportMain, -1);
        }
        if (reportSub !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.family_group = parseSelectInt(reportSub, -1);
        }
        if (unit1 !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.unit_1 = unit1.trim();
        }
        if (touched.has('nlu')) {
            patch.nlu = mnemonic.trim();
        }
        if (touched.has('dine')) {
            patch.price_1 = numOrDefault(dineIn, 0);
        }
        if (touched.has('take')) {
            patch.takeout_price_1 = numOrDefault(takeout, 0);
        }
        if (touched.has('lp')) {
            patch.rule_lnr_max_num = numOrDefault(limitPerson, 9999);
        }
        if (touched.has('lt')) {
            patch.rule_table_max_num = numOrDefault(limitTable, 9999);
        }
        if (touched.has('pal')) {
            patch.rule_lnr_limit_price = numOrDefault(priceAfterLimit, 0);
        }
        if (touched.has('minTime')) {
            patch.minimum_time = numOrDefault(minimumTime, 0);
        }
        if (batchClassIdSel !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE) {
            patch.class_id = Number(batchClassIdSel);
        }
        if (batchAllergenTouched) {
            patch.allergen = allergenKeys.filter((k) => ALLERGEN.some((a) => a.key === k));
        }
        if (batchIngredientTouched) {
            patch.other_detail_info = ingredientKeys.filter((k) => INGREDIENTS.some((a) => a.key === k));
        }

        if (hasNameCluster) {
            const dish = targets[0].dish;
            const merged = _.cloneDeep(buildItemI18nMergedFromDish(dish));
            if (touched.has('name1')) {
                _.set(merged, ['default', 'name1'], name1.trim());
            }
            if (touched.has('name2')) {
                _.set(merged, ['default', 'name2'], (name2.trim() || name1).trim());
            }
            if (touched.has('remarks')) {
                _.set(merged, ['default', 'remark'], remarks.trim());
            }
            if (touched.has('portion')) {
                _.set(merged, ['default', 'extra_remark'], portionNote.trim());
            }
            for (const t of touched) {
                if (!t.startsWith('i18n:')) {
                    continue;
                }
                const segs = t.split(':');
                if (segs.length !== 3) {
                    continue;
                }
                const lang = segs[1] as keyof DishItemI18nShape;
                const sub = segs[2];
                if (!['name1', 'name2', 'remark', 'extra_remark'].includes(sub)) {
                    continue;
                }
                const v = String(_.get(itemI18n, [lang, sub], '') ?? '');
                _.set(merged, [lang, sub], v);
            }
            const im = merged as Record<
                string,
                { name1?: string; name2?: string; remark?: string; extra_remark?: string } | undefined
            >;
            const dName1 = String(_.get(merged, ['default', 'name1'], '') ?? '').trim();
            const item_name1_api =
                dName1 ||
                (im.IT?.name1 || '').trim() ||
                (rk !== 'default' ? (im[rk]?.name1 || '').trim() : '') ||
                '';
            if (!item_name1_api) {
                GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_reuqired'));
                return;
            }
            const name2FromDefault = String(_.get(merged, ['default', 'name2'], '') ?? '').trim();
            const name2Final =
                name2FromDefault ||
                (im.IT?.name2 || '').trim() ||
                (rk !== 'default' ? (im[rk]?.name2 || '').trim() : '') ||
                item_name1_api;
            const defaultRemark = String(_.get(merged, ['default', 'remark'], '') ?? '').trim();
            patch.item_name1 = item_name1_api;
            patch.item_name2 = name2Final;
            patch.item_description =
                defaultRemark ||
                (im.IT?.remark || '').trim() ||
                (rk !== 'default' ? (im[rk]?.remark || '').trim() : '') ||
                '';
            patch.ext_dish_info =
                String(_.get(merged, ['default', 'extra_remark'], '') ?? '').trim() ||
                (im.IT?.extra_remark || '').trim() ||
                (rk !== 'default' ? (im[rk]?.extra_remark || '').trim() : '') ||
                '';
            patch.item_i18n = JSON.stringify(merged);
        }

        if (Object.keys(patch).length === 0) {
            GlobalNotification.info(i18n.t('menu_library_batch_no_field_changed'));
            return;
        }

        try {
            setSaveLoading(true);
            setLoading(true);
            const body = buildCloudDishLibraryBatchUpdateDishBody({
                cloudMenuId,
                cloudDishIds,
                patch,
            });
            await state.batchUpdateCloudDishes(body);
            if (state.isCloudMenuDetail) {
                await state.loadCloudMenuDetailData(cloudMenuId);
                await state.refreshCloudMenusAfterMutation();
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            onClose();
        } catch (e) {
            GlobalNotification.error(typeof e === 'string' ? e : i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            setSaveLoading(false);
        }
    }, [
        addDishDisplayLang,
        allergenKeys,
        batchAllergenTouched,
        batchClassIdSel,
        batchIngredientTouched,
        batchTextTouched,
        condimentGroup,
        condimentGroupsRaw,
        dishType,
        dineIn,
        ingredientKeys,
        itemI18n,
        limitPerson,
        limitTable,
        mnemonic,
        name1,
        name2,
        onClose,
        portionNote,
        printGroup,
        remarks,
        reportMain,
        reportSub,
        specialAttributes,
        state,
        takeout,
        taxGroup,
        unit1,
        priceAfterLimit,
        minimumTime,
        isStandaloneReadonly,
    ]);

    const handleSave = useCallback(async (options?: SaveDishOptions) => {
        if (isStandaloneReadonly) {
            return;
        }
        /** 云端批量设置走 `menus/dishes/batch-update`。 */
        if (
            (state.isCloudMenuDetail || state.createSource === 'cloud') &&
            state.menuLibraryAddDishDialogMode === 'batch'
        ) {
            await handleCloudBatchSave();
            return;
        }
        if (state.menuLibraryAddDishDialogMode === 'batch') {
            await handleBatchSave();
            return;
        }
        const skipCloseOnSuccessOpenCourseGroup = options?.skipCloseOnSuccessOpenCourseGroup === true;
        const tid = state.createMenuSubcategoryDetailTempId;
        const row = tid ? state.createMenuSubcategoryDrafts.find((r) => r.tempId === tid) : undefined;
        /** 云端新建弹窗 / 云端详情：写路径走 `cloud_slu_id`，不依赖本地 `dmi_slu_id` */
        const isCloudWriteContext = state.isCloudMenuDetail || state.createSource === 'cloud';
        const cloudSluIdForWrite = row?.cloud_slu_id ?? state.getCurrentCloudSluId();
        const sluIdFromDraft = row?.dmi_slu_id;
        const sluIdFromDetail = state.getCurrentSecondMenu()?.dmi_slu_id;
        const sluId = sluIdFromDraft ?? sluIdFromDetail;
        if (isCloudWriteContext) {
            if (!cloudSluIdForWrite) {
                GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
                return;
            }
        } else if (sluId == null) {
            GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
            return;
        }
        const rk = rbpLangKeyFromMenuLib(addDishDisplayLang);
        const merged = _.cloneDeep(itemI18n);
        _.set(merged, ['default', 'name1'], name1.trim());
        _.set(merged, ['default', 'name2'], (_.trim(name2) || name1).trim());
        _.set(merged, ['default', 'remark'], remarks.trim());
        _.set(merged, ['default', 'extra_remark'], portionNote.trim());
        const im = merged as Record<string, { name1?: string; name2?: string; remark?: string; extra_remark?: string } | undefined>;
        /** 与 classify-menu-detail 保存逻辑：`IT` 优先，再当前语种（见 RBP2 onSave） */
        const item_name1_api =
            name1.trim() ||
            (im.IT?.name1 || '').trim() ||
            (rk !== 'default' ? (im[rk]?.name1 || '').trim() : '') ||
            '';
        if (!item_name1_api) {
            GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_reuqired'));
            return;
        }
        const itemTypeNum = Number(dishType);
        if (itemTypeNum === TIMED_DISH_ITEM_TYPE) {
            if (numOrDefault(minimumTime, 0) <= 0) {
                GlobalNotification.error(i18n.t('menu_and_dishes_page_min_time_tip'));
                return;
            }
            if (numOrDefault(dineIn, 0) <= 0) {
                GlobalNotification.error(i18n.t('menu_and_dishes_page_min_price_tip'));
                return;
            }
        }
        const name1ForItaly =
            rk === 'default' ? name1 : String(_.get(itemI18n, [rk, 'name1'], '') ?? '');
        if (isItaly && !BASE_CHECK_TYPE.dishNameAndNlu2.regular.test(name1ForItaly)) {
            GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular2'));
            return;
        }
        const name2ForItaly =
            rk === 'default'
                ? name2 || name1
                : String(_.get(itemI18n, [rk, 'name2'], '') || _.get(itemI18n, [rk, 'name1'], '') || '');
        if (isItaly && !BASE_CHECK_TYPE.dishNameAndNlu.regular.test(name2ForItaly)) {
            GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular'));
            return;
        }
        if (!BASE_CHECK_TYPE.dishNameAndNlu.regular.test(mnemonic.trim())) {
            GlobalNotification.error(i18n.t('input_rule_text_enter_dish_name_and_nlu_regular'));
            return;
        }

        const name2Final =
            (_.trim(name2) || name1).trim() ||
            (im.IT?.name2 || '').trim() ||
            (rk !== 'default' ? (im[rk]?.name2 || '').trim() : '') ||
            item_name1_api;
        const item_description_api =
            remarks.trim() ||
            (im.IT?.remark || '').trim() ||
            (rk !== 'default' ? (im[rk]?.remark || '').trim() : '') ||
            null;
        const ext_dish_info_api =
            portionNote.trim() ||
            (im.IT?.extra_remark || '').trim() ||
            (rk !== 'default' ? (im[rk]?.extra_remark || '').trim() : '') ||
            '';
        const item_i18n_str = JSON.stringify(merged);
        const allergenStr = allergenKeys.filter((k) => ALLERGEN.some((a) => a.key === k)).join('*');
        const otherStr = ingredientKeys.filter((k) => INGREDIENTS.some((a) => a.key === k)).join('*');

        const editTarget = state.getAddDishEditTarget();
        const existingItemId = editTarget?.dish.item_id;
        const fromMenuDetail =
            (state.detailMainGroupId != null || state.isCloudMenuDetail) &&
            state.createMenuSubcategoryDetailTempId == null;

        try {
            if (skipCloseOnSuccessOpenCourseGroup) {
                suppressNextAddDishFormHydrateRef.current = true;
            }
            setSaveLoading(true);
            setLoading(true);

            const payload: PatchDishesParams = {
                slu_id: sluId as number,
                item_name1: item_name1_api,
                item_name2: name2Final,
                item_type: itemTypeNum,
                nlu: mnemonic.trim(),
                tax_group: parseSelectInt(taxGroup, -1),
                print_class: parseSelectInt(printGroup, -1),
                required_condiment: parseSelectInt(condimentGroup, -1),
                class_id: discountOn ? 6 : 3,
                major_group: parseSelectInt(reportMain, -1),
                family_group: parseSelectInt(reportSub, -1),
                special_attributes: parseSelectInt(specialAttributes, 0),
                unit_1: unit1.trim() || null,
                price_1: numOrDefault(dineIn, 0),
                takeout_price_1: numOrDefault(takeout, 0),
                rule_lnr_max_num: numOrDefault(limitPerson, 9999),
                rule_table_max_num: numOrDefault(limitTable, 9999),
                rule_lnr_limit_price: numOrDefault(priceAfterLimit, 0),
                minimum_time: itemTypeNum === TIMED_DISH_ITEM_TYPE ? numOrDefault(minimumTime, 0) : 0,
                item_description: item_description_api,
                ext_dish_info: ext_dish_info_api,
                item_i18n: item_i18n_str,
                allergen: allergenStr,
                other_detail_info: otherStr,
                rules_ignore_the_display: ignoreKwa ? 1 : 0,
                rule_lnr_limit_enable: continueAfterExceed ? 1 : 0,
                /** 与制作器新增一致：按二级菜单配置；新建弹窗此处暂无单栏位，默认 0 */
                not_joining_global_limit: 0,
            };

            /** 云端：新增走 `dishes/create`；编辑走 `menus/dishes/update`。 */
            if (isCloudWriteContext) {
                if (!cloudSluIdForWrite && editTarget == null) {
                    GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
                    return;
                }
                const cloudMenuId = state.detailCloudMenuId ?? state.createDialogPersistedCloudMenuId;
                if (!cloudMenuId) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
                const requiredCondimentId = parseSelectInt(condimentGroup, -1);
                const condimentGroupPayload = resolveCloudDishCondimentGroupForCreate(
                    condimentGroupsRaw,
                    requiredCondimentId,
                );
                const editCloudDishId =
                    state.menuLibraryDetailAddDishEditCloudDishId ??
                    getCloudDishIdFromMenuDish(editTarget?.dish);
                if (editTarget != null || editCloudDishId) {
                    if (!editCloudDishId) {
                        GlobalNotification.error(i18n.t('global_text_operate_failure'));
                        return;
                    }
                    const existingDish = editTarget?.dish;
                    /** 云端详情行可能带回 `cost_1`（本地 MenuDish 类型未声明） */
                    const existingCost = Number(
                        (existingDish as { cost_1?: number } | undefined)?.cost_1 ?? 0,
                    );
                    const updateBody = buildCloudDishLibraryUpdateDishBody({
                        cloudMenuId,
                        cloudDishId: editCloudDishId,
                        dishFields: {
                            item_name1: item_name1_api,
                            item_name2: name2Final,
                            item_type: itemTypeNum,
                            nlu: mnemonic.trim(),
                            tax_group: parseSelectInt(taxGroup, -1),
                            print_class: parseSelectInt(printGroup, -1),
                            class_id: discountOn ? 6 : 3,
                            major_group: parseSelectInt(reportMain, -1),
                            family_group: parseSelectInt(reportSub, -1),
                            special_attributes: parseSelectInt(specialAttributes, 0),
                            unit_1: unit1.trim(),
                            price_1: numOrDefault(dineIn, 0),
                            takeout_price_1: numOrDefault(takeout, 0),
                            rule_lnr_max_num: numOrDefault(limitPerson, 9999),
                            rule_table_max_num: numOrDefault(limitTable, 9999),
                            rule_lnr_limit_price: numOrDefault(priceAfterLimit, 0),
                            minimum_time: itemTypeNum === TIMED_DISH_ITEM_TYPE ? numOrDefault(minimumTime, 0) : 0,
                            item_description: item_description_api ?? '',
                            ext_dish_info: ext_dish_info_api,
                            item_i18n: item_i18n_str,
                            allergen: allergenKeys.filter((k) => ALLERGEN.some((a) => a.key === k)),
                            other_detail_info: ingredientKeys.filter((k) => INGREDIENTS.some((a) => a.key === k)),
                            /** 与 create / 本地编辑对齐 */
                            required_condiment: requiredCondimentId,
                            rules_ignore_the_display: ignoreKwa ? 1 : 0,
                            rule_lnr_limit_enable: continueAfterExceed ? 1 : 0,
                            /** 弹窗无单栏位；保留原行，避免编辑冲掉顶栏批量轮次结果 */
                            not_joining_global_limit: Number(existingDish?.not_joining_global_limit ?? 0) || 0,
                        },
                        cost_1: Number.isFinite(existingCost) ? existingCost : 0,
                        rule_lnr_enable: Number(existingDish?.rule_lnr_enable ?? 0) || 0,
                        weight_entry_required: Number(existingDish?.weight_entry_required ?? 0) || 0,
                        course_groups:
                            itemTypeNum === SET_MEAL_ITEM_TYPE
                                ? pendingCloudCourseGroups ??
                                  (existingDish as unknown as CloudDishLibraryMenuDetailDish | undefined)
                                      ?.course_groups ??
                                  []
                                : [],
                        condiment_group: condimentGroupPayload,
                    });
                    await state.updateCloudDish(updateBody);
                    const dishImageFinal = await state.resolveCloudDishImageForSave(
                        cloudMenuId,
                        editCloudDishId,
                        dishImageFileName.trim(),
                    );
                    state.resetMenuLibraryAddDishImageStaging(dishImageFinal);
                    setDishImageFileName(dishImageFinal);
                    if (tid) {
                        /** 云端菜品 string `id` 挂在行上，供再次编辑定位（本地 MenuDish.id 为 number） */
                        const updated = mergeCloudCreateDialogDishRowAfterUpdate({
                            existingDish,
                            payload,
                            dishImageFinal,
                            allergenKeys,
                            ingredientKeys,
                            cloudDishId: editCloudDishId,
                            courseGroups:
                                itemTypeNum === SET_MEAL_ITEM_TYPE
                                    ? pendingCloudCourseGroups ??
                                      (existingDish as unknown as CloudDishLibraryMenuDetailDish | undefined)
                                          ?.course_groups
                                    : undefined,
                        });
                        if (editTarget != null) {
                            state.replaceCreateMenuSubcategoryDishAtIndex(editTarget.index, updated);
                        } else {
                            /** 首次保存未关弹窗再改时可能无 editTarget，按云端 id 回写草稿行 */
                            const list = state.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
                            const idx = list.findIndex(
                                (d) => getCloudDishIdFromMenuDish(d) === editCloudDishId,
                            );
                            if (idx >= 0) {
                                state.replaceCreateMenuSubcategoryDishAtIndex(idx, updated);
                            }
                        }
                    } else if (state.isCloudMenuDetail) {
                        await state.loadCloudMenuDetailData(cloudMenuId);
                        await state.refreshCloudMenusAfterMutation();
                    }
                    GlobalNotification.success(i18n.t('global_text_operate_success'));
                    if (skipCloseOnSuccessOpenCourseGroup) {
                        setPendingCloudDishId(editCloudDishId);
                        setCourseGroupDialogOpen(true);
                        void state.ensureCloudCourseGroupPickerMenusLoaded();
                        return;
                    }
                    onClose();
                    return;
                }
                if (!cloudSluIdForWrite) {
                    GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
                    return;
                }
                const cloudDishPayload: CloudDishLibraryCreateDishBody = {
                    cloud_slu_id: cloudSluIdForWrite,
                    item_name1: item_name1_api,
                    item_name2: name2Final,
                    item_type: itemTypeNum,
                    nlu: mnemonic.trim(),
                    tax_group: parseSelectInt(taxGroup, -1),
                    print_class: parseSelectInt(printGroup, -1),
                    required_condiment: requiredCondimentId,
                    class_id: discountOn ? 6 : 3,
                    major_group: parseSelectInt(reportMain, -1),
                    family_group: parseSelectInt(reportSub, -1),
                    special_attributes: parseSelectInt(specialAttributes, 0),
                    /** 云端校验要求 string；空规格传 `''`，勿传 null */
                    unit_1: unit1.trim(),
                    price_1: numOrDefault(dineIn, 0),
                    takeout_price_1: numOrDefault(takeout, 0),
                    rule_lnr_max_num: numOrDefault(limitPerson, 9999),
                    rule_table_max_num: numOrDefault(limitTable, 9999),
                    rule_lnr_limit_price: numOrDefault(priceAfterLimit, 0),
                    minimum_time: itemTypeNum === TIMED_DISH_ITEM_TYPE ? numOrDefault(minimumTime, 0) : 0,
                    item_description: item_description_api ?? '',
                    ext_dish_info: ext_dish_info_api,
                    item_i18n: item_i18n_str,
                    allergen: allergenKeys.filter((k) => ALLERGEN.some((a) => a.key === k)),
                    other_detail_info: ingredientKeys.filter((k) => INGREDIENTS.some((a) => a.key === k)),
                    rules_ignore_the_display: ignoreKwa ? 1 : 0,
                    rule_lnr_limit_enable: continueAfterExceed ? 1 : 0,
                    not_joining_global_limit: 0,
                };
                if (condimentGroupPayload) {
                    cloudDishPayload.condiment_group = condimentGroupPayload;
                }
                if (itemTypeNum === SET_MEAL_ITEM_TYPE && pendingCloudCourseGroups?.length) {
                    cloudDishPayload.course_groups = pendingCloudCourseGroups;
                }
                const cloudDishId = await state.createCloudDish(cloudDishPayload);
                if (!cloudDishId) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
                const dishImageFinal = await state.resolveCloudDishImageForSave(
                    cloudMenuId,
                    cloudDishId,
                    dishImageFileName.trim(),
                );
                state.resetMenuLibraryAddDishImageStaging(dishImageFinal);
                setDishImageFileName(dishImageFinal);
                if (tid) {
                    /** 新建弹窗草稿列表暂用云端 string id 占位 */
                    const saved = {
                        ...payload,
                        item_id: undefined,
                        dish_image: dishImageFinal,
                        allergen: allergenKeys,
                        other_detail_info: ingredientKeys,
                        id: cloudDishId,
                        course_groups:
                            itemTypeNum === SET_MEAL_ITEM_TYPE ? pendingCloudCourseGroups ?? undefined : undefined,
                    } as unknown as MenuDish;
                    state.appendCreateMenuSubcategoryDish(saved);
                } else if (state.isCloudMenuDetail) {
                    await state.loadCloudMenuDetailData(cloudMenuId);
                    await state.refreshCloudMenusAfterMutation();
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                if (skipCloseOnSuccessOpenCourseGroup) {
                    setPendingCloudDishId(cloudDishId);
                    state.menuLibraryDetailAddDishEditCloudDishId = cloudDishId;
                    setCourseGroupDialogOpen(true);
                    void state.ensureCloudCourseGroupPickerMenusLoaded();
                    return;
                }
                onClose();
                return;
            }
            if (existingItemId != null && editTarget != null) {
                /** 保存菜品接口不传 `dish_image`；静态图由 `resolveMenuLibraryAddDishImageForSave` 单独请求，结果仅写回前端行数据 */
                const dishImageForLocal =
                    existingItemId > 0
                        ? await state.resolveMenuLibraryAddDishImageForSave(
                            rbpId,
                            existingItemId,
                            dishImageFileName.trim(),
                        )
                        : dishImageFileName.trim();
                const patchPayload: PatchDishesParams = {
                    ...payload,
                    item_id: existingItemId,
                };
                const patchRes = await apiManage_rbp.patch_dishes([patchPayload], false, rbpId);
                if (_.get(patchRes, 'error')) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
                state.resetMenuLibraryAddDishImageStaging(dishImageForLocal);
                setDishImageFileName(dishImageForLocal);
                const updated: MenuDish = {
                    ...editTarget.dish,
                    ...patchPayload,
                    item_id: existingItemId,
                    dish_image: dishImageForLocal,
                    allergen: allergenKeys,
                    other_detail_info: ingredientKeys,
                };
                if (tid) {
                    state.replaceCreateMenuSubcategoryDishAtIndex(editTarget.index, updated);
                } else if (fromMenuDetail && state.detailFirstMenu?.main_group_id != null) {
                    await state.loadDetailData(state.detailFirstMenu.main_group_id);
                    await state.refreshLocalMenusAfterMutation();
                }
                if (skipCloseOnSuccessOpenCourseGroup && existingItemId != null && existingItemId > 0) {
                    /** 与 post 分支的 insertId 一致：保证 courseGroupDishItemId 在 patch 后仍 >0，弹窗能 open */
                    setPendingCourseGroupItemId(existingItemId);
                }
            } else {
                const result = await apiManage_rbp.post_dishes(payload, rbpId);
                if (_.get(result, 'error')) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
                const insertId = result.insertId;
                const dishImageFinal = await state.resolveMenuLibraryAddDishImageForSave(
                    rbpId,
                    insertId,
                    dishImageFileName.trim(),
                );
                state.resetMenuLibraryAddDishImageStaging(dishImageFinal);
                setDishImageFileName(dishImageFinal);
                const saved: MenuDish = {
                    ...payload,
                    item_id: insertId,
                    dish_image: dishImageFinal,
                    allergen: allergenKeys,
                    other_detail_info: ingredientKeys,
                };
                if (tid) {
                    if (editTarget != null) {
                        state.replaceCreateMenuSubcategoryDishAtIndex(editTarget.index, saved);
                    } else {
                        state.appendCreateMenuSubcategoryDish(saved);
                    }
                    if (skipCloseOnSuccessOpenCourseGroup) {
                        state.promoteCreateSubcategoryAddDishEditAfterSave(insertId);
                    }
                } else if (fromMenuDetail && state.detailFirstMenu?.main_group_id != null) {
                    /**
                     * 必须在 loadDetailData 之前 promote，并传入 post 组装的 saved：列表尚未含新行时 getAddDishEditTarget 可回退到 menuLibraryDetailPostSavedDishFallback。
                     */
                    state.promoteMenuLibraryDetailAddDishEditAfterFirstSave(insertId, saved);
                    await state.loadDetailData(state.detailFirstMenu.main_group_id);
                    await state.refreshLocalMenusAfterMutation();
                }
                if (skipCloseOnSuccessOpenCourseGroup && insertId > 0) {
                    /** 列表未含新菜时 `getAddDishEditTarget` 仍可能为 null，用 insertId 保证套餐弹窗能打开 */
                    setPendingCourseGroupItemId(insertId);
                }
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (skipCloseOnSuccessOpenCourseGroup) {
                setCourseGroupDialogOpen(true);
            } else {
                setPendingCourseGroupItemId(null);
                onClose();
            }
        } catch (e) {
            suppressNextAddDishFormHydrateRef.current = false;
            const msg =
                e instanceof Error
                    ? e.message
                    : typeof e === 'string'
                        ? e
                        : i18n.t('global_text_operate_failure');
            GlobalNotification.error(msg);
        } finally {
            setLoading(false);
            setSaveLoading(false);
            if (skipCloseOnSuccessOpenCourseGroup) {
                setTimeout(() => {
                    suppressNextAddDishFormHydrateRef.current = false;
                }, 0);
            }
        }
    }, [
        addDishDisplayLang,
        allergenKeys,
        condimentGroup,
        condimentGroupsRaw,
        continueAfterExceed,
        dishType,
        discountOn,
        dineIn,
        ignoreKwa,
        ingredientKeys,
        itemI18n,
        limitPerson,
        limitTable,
        minimumTime,
        mnemonic,
        name1,
        name2,
        onClose,
        portionNote,
        printGroup,
        remarks,
        reportMain,
        reportSub,
        rbpId,
        specialAttributes,
        state,
        takeout,
        taxGroup,
        unit1,
        isItaly,
        priceAfterLimit,
        handleBatchSave,
        handleCloudBatchSave,
        isStandaloneReadonly,
        addDishEditTargetResolved,
        dishImageFileName,
        pendingCloudCourseGroups,
    ]);

    const openCourseGroupDialog = useCallback(async () => {
        if (isStandaloneReadonly) {
            return;
        }
        if (isBatchMode) {
            return;
        }
        const d = addDishEditTargetResolved?.dish;
        const formCombo = Number(dishType) === SET_MEAL_ITEM_TYPE;
        const serverCombo = d != null && Number(d.item_type) === SET_MEAL_ITEM_TYPE;
        if (!formCombo && !serverCombo) {
            GlobalNotification.error(i18n.t('menu_library_course_group_need_combo_type'));
            return;
        }
        const isCloudWrite = state.isCloudMenuDetail || state.createSource === 'cloud';
        if (isCloudWrite) {
            const cloudId =
                pendingCloudDishId ||
                state.menuLibraryDetailAddDishEditCloudDishId ||
                getCloudDishIdFromMenuDish(d);
            if (cloudId && formCombo && (serverCombo || d == null || pendingCloudDishId)) {
                setCourseGroupDialogOpen(true);
                void state.ensureCloudCourseGroupPickerMenusLoaded();
                return;
            }
            setCourseGroupEntryLoading(true);
            try {
                await handleSave({ skipCloseOnSuccessOpenCourseGroup: true });
            } finally {
                setCourseGroupEntryLoading(false);
            }
            return;
        }
        const resolved = state.getResolvedCourseGroupDishItemId();
        const id = resolved > 0 ? resolved : pendingCourseGroupItemId ?? 0;
        /**
         * 可直接开弹窗：已有 item_id 且表单为套餐，且（服务端已是套餐 / 或列表未同步但 state 已有 id，如 promote、pending 路径）。
         * 若行数据存在且服务端尚非套餐，说明用户刚在表单改为套餐，必须先 patch 再开弹窗。
         */
        if (id > 0 && formCombo && (serverCombo || d == null)) {
            setCourseGroupDialogOpen(true);
            return;
        }
        setCourseGroupEntryLoading(true);
        try {
            await handleSave({ skipCloseOnSuccessOpenCourseGroup: true });
        } finally {
            setCourseGroupEntryLoading(false);
        }
    }, [
        dishType,
        state,
        handleSave,
        pendingCourseGroupItemId,
        pendingCloudDishId,
        isBatchMode,
        isStandaloneReadonly,
        addDishEditTargetResolved,
    ]);

    const courseGroupDishItemId = (() => {
        const r = state.getResolvedCourseGroupDishItemId();
        return r > 0 ? r : pendingCourseGroupItemId ?? 0;
    })();

    /** 云端套餐弹窗用的云端菜品 id */
    const courseGroupCloudDishId =
        pendingCloudDishId ||
        state.menuLibraryDetailAddDishEditCloudDishId ||
        getCloudDishIdFromMenuDish(addDishEditTargetResolved?.dish) ||
        '';

    /** 云端套餐写路径 */
    const isCloudCourseGroupMode = state.isCloudMenuDetail || state.createSource === 'cloud';

    /**
     * 套餐弹窗保存：立刻 `dishes/update` 提交完整 course_groups。
     * @param courseGroups - 套餐组对象数组
     */
    const handleCloudCourseGroupSave = useCallback(
        async (courseGroups: CloudDishLibraryCourseGroup[]) => {
            const cloudMenuId = state.detailCloudMenuId ?? state.createDialogPersistedCloudMenuId;
            const cloudDishId = courseGroupCloudDishId;
            if (!cloudMenuId || !cloudDishId) {
                throw new Error(i18n.t('global_text_operate_failure'));
            }
            const name2Final = editDishName2 ? name2.trim() : name1.trim();
            const item_i18n_str = JSON.stringify(itemI18n);
            const item_description_api = remarks.trim() || '';
            const ext_dish_info_api = portionNote.trim();
            const existingDish = addDishEditTargetResolved?.dish;
            const existingCost = Number((existingDish as { cost_1?: number } | undefined)?.cost_1 ?? 0);
            const requiredCondimentId = parseSelectInt(condimentGroup, -1);
            const condimentGroupPayload = resolveCloudDishCondimentGroupForCreate(
                condimentGroupsRaw,
                requiredCondimentId,
            );
            const updateBody = buildCloudDishLibraryUpdateDishBody({
                cloudMenuId,
                cloudDishId,
                dishFields: {
                    item_name1: name1.trim(),
                    item_name2: name2Final,
                    item_type: SET_MEAL_ITEM_TYPE,
                    nlu: mnemonic.trim(),
                    tax_group: parseSelectInt(taxGroup, -1),
                    print_class: parseSelectInt(printGroup, -1),
                    class_id: discountOn ? 6 : 3,
                    major_group: parseSelectInt(reportMain, -1),
                    family_group: parseSelectInt(reportSub, -1),
                    special_attributes: parseSelectInt(specialAttributes, 0),
                    unit_1: unit1.trim(),
                    price_1: numOrDefault(dineIn, 0),
                    takeout_price_1: numOrDefault(takeout, 0),
                    rule_lnr_max_num: numOrDefault(limitPerson, 9999),
                    rule_table_max_num: numOrDefault(limitTable, 9999),
                    rule_lnr_limit_price: numOrDefault(priceAfterLimit, 0),
                    minimum_time: 0,
                    item_description: item_description_api,
                    ext_dish_info: ext_dish_info_api,
                    item_i18n: item_i18n_str,
                    allergen: allergenKeys.filter((k) => ALLERGEN.some((a) => a.key === k)),
                    other_detail_info: ingredientKeys.filter((k) => INGREDIENTS.some((a) => a.key === k)),
                    /** 与 create / 本地编辑对齐 */
                    required_condiment: requiredCondimentId,
                    rules_ignore_the_display: ignoreKwa ? 1 : 0,
                    rule_lnr_limit_enable: continueAfterExceed ? 1 : 0,
                    /** 弹窗无单栏位；保留原行，避免编辑冲掉顶栏批量轮次结果 */
                    not_joining_global_limit: Number(existingDish?.not_joining_global_limit ?? 0) || 0,
                },
                cost_1: Number.isFinite(existingCost) ? existingCost : 0,
                rule_lnr_enable: Number(existingDish?.rule_lnr_enable ?? 0) || 0,
                weight_entry_required: Number(existingDish?.weight_entry_required ?? 0) || 0,
                course_groups: courseGroups,
                condiment_group: condimentGroupPayload,
            });
            await state.updateCloudDish(updateBody);
            setPendingCloudCourseGroups(courseGroups);
            if (state.isCloudMenuDetail) {
                await state.loadCloudMenuDetailData(cloudMenuId);
                await state.refreshCloudMenusAfterMutation();
            }
        },
        [
            state,
            courseGroupCloudDishId,
            editDishName2,
            name1,
            name2,
            itemI18n,
            remarks,
            portionNote,
            addDishEditTargetResolved,
            condimentGroup,
            condimentGroupsRaw,
            mnemonic,
            taxGroup,
            printGroup,
            discountOn,
            reportMain,
            reportSub,
            specialAttributes,
            unit1,
            dineIn,
            takeout,
            limitPerson,
            limitTable,
            priceAfterLimit,
            allergenKeys,
            ingredientKeys,
            ignoreKwa,
            continueAfterExceed,
        ],
    );

    const showTimedMinTimeField =
        dishType !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE && Number(dishType) === TIMED_DISH_ITEM_TYPE;

    /** 仅按表单当前菜品类型显示「编辑套餐菜品」；勿合并服务端行 item_type，否则编辑时改类型后按钮仍显示 */
    const showComboUi =
        !isStandaloneReadonly &&
        !isBatchMode &&
        dishType !== MENU_LIB_ADD_DISH_BATCH_NO_CHANGE &&
        Number(dishType) === SET_MEAL_ITEM_TYPE;
    /** 保存后若回填瞬间把 dishType 改非套餐，仍保留挂载，否则 open 了但子组件被卸载会看不到套餐弹窗 */
    const mountCourseGroupDialog =
        !isStandaloneReadonly &&
        !isBatchMode &&
        (showComboUi ||
            (courseGroupDialogOpen &&
                (isCloudCourseGroupMode ? !!courseGroupCloudDishId : courseGroupDishItemId > 0)));
    /** 与 `MenuDish.bigFoodImage` / `foodImage` 或商店模板 `image_url` / `icon_url` / `dish_image*` 映射一致 */
    const dishThumbSrc = (addDishEditTargetResolved?.dish.bigFoodImage || addDishEditTargetResolved?.dish.foodImage || '')
        .trim();
    return (
        <Box
            className={['menu-library-add-dish', isStandaloneReadonly ? 'menu-library-add-dish--standalone-readonly' : '']
                .filter(Boolean)
                .join(' ')}
        >
            <Box className="menu-library-add-dish__scroll">
                {/**
                 * standalone 只读：`pointer-events` 仅加在内层，避免加在 `__scroll` 上导致滚轮无法触发滚动。
                 * 展示语言区仍通过 SCSS 对 `.menu-library-add-dish__lang-display-control` 恢复 `pointer-events: auto`。
                 */}
                <Box
                    className={[
                        'menu-library-add-dish__scroll-inner',
                        isStandaloneReadonly ? 'menu-library-add-dish__scroll-inner--readonly' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                <Box className="menu-library-add-dish__top">
                    {isBatchMode ? (
                        <Box className="menu-library-add-dish__thumb" aria-hidden />
                    ) : isStandaloneReadonly ? (
                        <Box className="menu-library-add-dish__thumb" aria-hidden>
                            {dishThumbSrc ? (
                                <img className="menu-library-add-dish__thumb-img" src={dishThumbSrc} alt="" />
                            ) : null}
                        </Box>
                    ) : (
                        <Box className="menu-library-add-dish__thumb">
                            <RestaurantStaticImageUpload
                                stringId={rbpId}
                                kind="dish-image"
                                uploadEntityId={state.getAddDishEditTarget()?.dish.item_id ?? null}
                                imageRef={dishImageFileName}
                                displayUrl={dishImageDisplayUrl}
                                deferStaticUpload
                                onStagedStaticFile={onStagedDishImageFile}
                                onDeferredStaticRemove={onDeferredDishImageRemove}
                                disabled={saveLoading}
                                avatarSize={145}
                                sx={{ width: '100%', height: '100%' }}
                            />
                        </Box>
                    )}
                    <Box className="menu-library-add-dish__top-fields menu-library-add-dish__grid menu-library-add-dish__grid--top">
                        <Box className="menu-library-add-dish__cell menu-library-add-dish__cell--lang-field">
                            <Typography
                                component="span"
                                className="menu-library-add-dish__cell-label menu-library-add-dish__lang-display"
                            >
                                {i18n.t('menu_library_add_dish_dish_lang_display')}
                            </Typography>
                            <Box className="menu-library-add-dish__lang-display-control menu-library-add-dish__lang-display-control--primary-plain">
                                <MenuLanguageMenuButton
                                    state={state}
                                    value={addDishDisplayLang}
                                    onChange={setAddDishDisplayLang}
                                />
                            </Box>
                        </Box>
                        <Box className="menu-library-add-dish__cell menu-library-add-dish__cell--name-field">
                            <TextField
                                className="menu-library-add-dish__name-textfield"
                                label={i18n.t('menu_library_add_dish_name1')}
                                value={getI18nField('item_name1')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={nameTextFieldSx}
                                placeholder={
                                    isBatchMode && !batchTextTouched.has('name1') ? batchNoChangeLabel : undefined
                                }
                                InputLabelProps={isBatchMode ? { shrink: true } : undefined}
                                onChange={(e) => {
                                    setI18nField('item_name1', e.target.value);
                                }}
                            />
                        </Box>
                        <Box className="menu-library-add-dish__cell menu-library-add-dish__cell--name-field">
                            <TextField
                                className="menu-library-add-dish__name-textfield"
                                label={i18n.t('menu_library_add_dish_name2')}
                                value={getI18nField('item_name2')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={nameTextFieldSx}
                                placeholder={
                                    isBatchMode && !batchTextTouched.has('name2') ? batchNoChangeLabel : undefined
                                }
                                InputLabelProps={isBatchMode ? { shrink: true } : undefined}
                                onChange={(e) => {
                                    setI18nField('item_name2', e.target.value);
                                }}
                            />
                        </Box>
                        {topFieldRowSecond.map((field) => (
                            <Box key={field.key} className="menu-library-add-dish__cell">
                                <Typography
                                    className={[
                                        'menu-library-add-dish__cell-label',
                                        field.multilineLabel ? 'menu-library-add-dish__cell-label--multiline' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    component="span"
                                >
                                    {i18n.t(field.labelKey)}
                                </Typography>
                                <Box
                                    className={[
                                        'menu-library-add-dish__cell-control',
                                        field.select ? 'menu-library-add-dish__cell-control--select' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    {field.node}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box className="menu-library-add-dish__grid">
                    {mainGridFields.flatMap((f) => {
                        const fieldNode =
                            f.kind === 'text' ? (
                                <FormFieldCell key={f.key} labelKey={f.labelKey}>
                                    <CommonInput
                                        inputType="INPUT"
                                        hiddenLabel
                                        fullWidth
                                        size="small"
                                        value={f.value}
                                        placeholder={f.placeholder}
                                        onChange={(e) => f.setValue(String((e.target as HTMLInputElement).value))}
                                    />
                                </FormFieldCell>
                            ) : f.kind === 'nonNegDecimal' ? (
                                <FormFieldCell
                                    key={f.key}
                                    labelKey={f.labelKey}
                                    cellClassName="menu-library-add-dish__cell--split-value"
                                >
                                    <CommonInput
                                        inputType="INPUT"
                                        hiddenLabel
                                        fullWidth
                                        size="small"
                                        type="text"
                                        inputProps={{ inputMode: 'decimal' }}
                                        className="menu-library-add-dish__number-input"
                                        value={f.value}
                                        placeholder={f.placeholder}
                                        onChange={(e) => {
                                            const raw = sanitizeNonNegativeDecimalInput(
                                                String((e.target as HTMLInputElement).value),
                                            );
                                            if (f.key === 'take') {
                                                setTakeoutUserEdited(raw.trim() !== '');
                                            }
                                            f.setValue(raw);
                                            if (f.key === 'dine' && !takeoutUserEdited) {
                                                setTakeout(raw);
                                            }
                                        }}
                                        onBlur={() => {
                                            const c = clampNonNegativeDecimalString(f.value);
                                            f.setValue(c);
                                            if (f.syncTakeoutOnBlur && !takeoutUserEdited) {
                                                setTakeout(c);
                                            }
                                        }}
                                    />
                                </FormFieldCell>
                            ) : f.kind === 'nonNegInt' ? (
                                <FormFieldCell
                                    key={f.key}
                                    labelKey={f.labelKey}
                                    cellClassName="menu-library-add-dish__cell--split-value"
                                >
                                    <CommonInput
                                        inputType="INPUT"
                                        hiddenLabel
                                        fullWidth
                                        size="small"
                                        type="text"
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                        className="menu-library-add-dish__number-input"
                                        value={f.value}
                                        placeholder={f.placeholder}
                                        onChange={(e) =>
                                            f.setValue(
                                                sanitizeNonNegativeIntInput(
                                                    String((e.target as HTMLInputElement).value),
                                                ),
                                            )
                                        }
                                        onBlur={() => f.setValue(clampNonNegativeIntString(f.value))}
                                    />
                                </FormFieldCell>
                            ) : (
                                <FormFieldCell key={f.key} labelKey={f.labelKey} multilineLabel={f.multilineLabel} select>
                                    <CommonInput
                                        inputType="SELECT"
                                        hiddenLabel
                                        fullWidth
                                        size="small"
                                        value={f.value}
                                        items={f.items}
                                        disabled={refLoading}
                                        onChange={(e) => f.setValue(String((e.target as HTMLInputElement).value))}
                                    />
                                </FormFieldCell>
                            );
                        if (f.key === 'tag') {
                            return [
                                fieldNode,
                                isBatchMode ? (
                                    <Box
                                        key="discount-batch"
                                        className="menu-library-add-dish__cell menu-library-add-dish__toggle-cell"
                                    >
                                        <Typography component="span" className="menu-library-add-dish__cell-label">
                                            {i18n.t('menu_library_add_dish_is_discount')}
                                        </Typography>
                                        <Box className="menu-library-add-dish__cell-control menu-library-add-dish__cell-control--select">
                                            <CommonInput
                                                inputType="SELECT"
                                                hiddenLabel
                                                fullWidth
                                                size="small"
                                                value={batchClassIdSel}
                                                items={batchDiscountSelectItems}
                                                disabled={refLoading}
                                                onChange={(e) =>
                                                    setBatchClassIdSel(String((e.target as HTMLInputElement).value))
                                                }
                                            />
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box
                                        key="discount-toggle"
                                        className="menu-library-add-dish__cell menu-library-add-dish__toggle-cell"
                                    >
                                        <Typography component="span" className="menu-library-add-dish__cell-label">
                                            {i18n.t('menu_library_add_dish_is_discount')}
                                        </Typography>
                                        <AntSwitch checked={discountOn} onChange={(_, v) => setDiscountOn(v)} />
                                    </Box>
                                ),
                            ];
                        }
                        return [fieldNode];
                    })}

                    {showTimedMinTimeField ? (
                        <FormFieldCell
                            labelKey="menu_and_dishes_page_min_time"
                            cellClassName="menu-library-add-dish__cell--split-value"
                        >
                            <CommonInput
                                inputType="INPUT"
                                hiddenLabel
                                fullWidth
                                size="small"
                                type="text"
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                className="menu-library-add-dish__number-input"
                                value={
                                    isBatchMode && !batchTextTouched.has('minTime') ? '' : minimumTime
                                }
                                placeholder={
                                    isBatchMode && !batchTextTouched.has('minTime')
                                        ? batchNoChangeLabel
                                        : undefined
                                }
                                onChange={(e) => {
                                    if (isBatchMode) {
                                        markBatchTextTouched('minTime');
                                    }
                                    setMinimumTime(
                                        sanitizeNonNegativeIntInput(
                                            String((e.target as HTMLInputElement).value),
                                        ),
                                    );
                                }}
                                onBlur={() => setMinimumTime(clampNonNegativeIntString(minimumTime))}
                            />
                        </FormFieldCell>
                    ) : null}

                    {!isBatchMode && showComboUi ? (
                        <Box
                            component="button"
                            type="button"
                            aria-label={i18n.t('menu_library_add_dish_edit_combo')}
                            aria-busy={courseGroupEntryLoading}
                            disabled={courseGroupEntryLoading}
                            className="menu-library-add-dish__cell menu-library-add-dish__toggle-cell menu-library-add-dish__combo-row"
                            onClick={() => void openCourseGroupDialog()}
                        >
                            <Typography component="span" className="menu-library-add-dish__cell-label">
                                {i18n.t('menu_library_add_dish_edit_combo')}
                            </Typography>
                            {courseGroupEntryLoading ? (
                                <CircularProgress
                                    size={22}
                                    thickness={4}
                                    className="menu-library-add-dish__combo-fastfood-icon"
                                    sx={{ color: comboIconColor }}
                                    aria-hidden
                                />
                            ) : (
                                <FastfoodIcon
                                    className="menu-library-add-dish__combo-fastfood-icon"
                                    sx={{ color: comboIconColor, fontSize: 28 }}
                                    aria-hidden
                                />
                            )}
                        </Box>
                    ) : null}

                    <Box className="menu-library-add-dish__cell menu-library-add-dish__cell--full menu-library-add-dish__cell--remark">
                        <Typography component="span" className="menu-library-add-dish__cell-label">
                            {i18n.t('menu_library_add_dish_remarks')}
                        </Typography>
                        <Box className="menu-library-add-dish__cell-control menu-library-add-dish__remark-input">
                            <CommonInput
                                inputType="INPUT"
                                hiddenLabel
                                fullWidth
                                multiline
                                minRows={1}
                                size="small"
                                value={getI18nField('item_description')}
                                placeholder={
                                    isBatchMode && !batchTextTouched.has('remarks') ? batchNoChangeLabel : undefined
                                }
                                onChange={(e) =>
                                    setI18nField('item_description', String((e.target as HTMLInputElement).value))
                                }
                            />
                        </Box>
                    </Box>

                    <Box className="menu-library-add-dish__cell menu-library-add-dish__cell--full menu-library-add-dish__cell--selector-row">
                        <Typography component="span" className="menu-library-add-dish__cell-label" sx={{ pt: 0.5 }}>
                            {i18n.t('menu_library_add_dish_allergen_section')}
                        </Typography>
                        <Box className="menu-library-add-dish__cell-control menu-library-add-dish__cell-control--mul">
                            <SelectInputMultiple
                                className="menu-library-add-dish__mul-select"
                                options={allergenOptions}
                                value={allergenValue}
                                limitTagsNum={-1}
                                placeholder={
                                    isBatchMode && !batchAllergenTouched ? batchNoChangeLabel : undefined
                                }
                                onChange={(opts) => {
                                    if (isBatchMode) {
                                        setBatchAllergenTouched(true);
                                    }
                                    setAllergenKeys(opts.map((o) => o.value));
                                }}
                            />
                        </Box>
                    </Box>

                    <Box className="menu-library-add-dish__cell menu-library-add-dish__cell--full menu-library-add-dish__cell--selector-row">
                        <Typography component="span" className="menu-library-add-dish__cell-label" sx={{ pt: 0.5 }}>
                            {i18n.t('menu_library_add_dish_other_tags_section')}
                        </Typography>
                        <Box className="menu-library-add-dish__cell-control menu-library-add-dish__cell-control--mul">
                            <SelectInputMultiple
                                className="menu-library-add-dish__mul-select"
                                options={ingredientOptions}
                                limitTagsNum={-1}
                                value={ingredientValue}
                                placeholder={
                                    isBatchMode && !batchIngredientTouched ? batchNoChangeLabel : undefined
                                }
                                onChange={(opts) => {
                                    if (isBatchMode) {
                                        setBatchIngredientTouched(true);
                                    }
                                    setIngredientKeys(opts.map((o) => o.value));
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                </Box>
            </Box>

            {!isStandaloneReadonly ? (
            <Box className="menu-library-add-dish__footer">
                {!isBatchMode && addDishEditTargetResolved != null ? (
                    <>
                        <AlertDialog
                            open={deleteConfirmOpen}
                            variant="delete"
                            size="sm"
                            title={i18n.t('menu_library_detail_delete_dish')}
                            bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                            highlightText={(() => {
                                const d = addDishEditTargetResolved?.dish;
                                return d ? state.getDishDisplayName(d) : '—';
                            })()}
                            confirmText={i18n.t('menu_library_detail_delete_dish')}
                            onCancel={() => setDeleteConfirmOpen(false)}
                            onConfirm={() => {
                                setDeleteConfirmOpen(false);
                                void state.deleteAddDishEditingItem();
                            }}
                        />
                        <Button
                            type="button"
                            variant="outlined"
                            color="error"
                            className="menu-library-add-dish__delete-btn"
                            disabled={saveLoading}
                            startIcon={<BaselineDeleteForeverIcon className="menu-library-add-dish__delete-icon" aria-hidden />}
                            onClick={() => setDeleteConfirmOpen(true)}
                        >
                            {i18n.t('menu_library_detail_delete_dish')}
                        </Button>
                    </>
                ) : null}
                {isBatchMode ? (
                    <>
                        <Box className="menu-library-add-dish__footer-box menu-library-add-dish__footer-box--batch-select">
                            <Typography component="div" className="menu-library-add-dish__footer-label">
                                {i18n.t('menu_library_add_dish_ignore_kwa')}
                            </Typography>
                            <CommonInput
                                inputType="SELECT"
                                hiddenLabel
                                fullWidth
                                size="small"
                                className="menu-library-add-dish__footer-batch-select"
                                value={batchIgnoreKwaSel}
                                items={batchYesNoSelectItems}
                                onChange={(e) =>
                                    setBatchIgnoreKwaSel(String((e.target as HTMLInputElement).value))
                                }
                            />
                        </Box>
                        <Box className="menu-library-add-dish__footer-box menu-library-add-dish__footer-box--batch-select">
                            <Typography component="div" className="menu-library-add-dish__footer-label">
                                {i18n.t('menu_library_add_dish_continue_after_exceed')}
                            </Typography>
                            <CommonInput
                                inputType="SELECT"
                                hiddenLabel
                                fullWidth
                                size="small"
                                className="menu-library-add-dish__footer-batch-select"
                                value={batchLimitEnableSel}
                                items={batchYesNoSelectItems}
                                onChange={(e) =>
                                    setBatchLimitEnableSel(String((e.target as HTMLInputElement).value))
                                }
                            />
                        </Box>
                    </>
                ) : (
                    <>
                        <Box className="menu-library-add-dish__footer-box">
                            <Typography component="div" className="menu-library-add-dish__footer-label">
                                {i18n.t('menu_library_add_dish_ignore_kwa')}
                            </Typography>
                            <AntSwitch checked={ignoreKwa} onChange={(_, v) => setIgnoreKwa(v)} />
                        </Box>
                        <Box className="menu-library-add-dish__footer-box">
                            <Typography component="div" className="menu-library-add-dish__footer-label">
                                {i18n.t('menu_library_add_dish_continue_after_exceed')}
                            </Typography>
                            <AntSwitch checked={continueAfterExceed} onChange={(_, v) => setContinueAfterExceed(v)} />
                        </Box>
                    </>
                )}
                <LoadingOverlayButton
                    type="button"
                    variant="contained"
                    fullWidth
                    className="menu-library-add-dish__save-btn"
                    loading={saveLoading}
                    disabled={refLoading}
                    loadingIndicatorSize={22}
                    onClick={() => void handleSave()}
                    startIcon={<SaveIcon className="menu-library-add-dish__save-icon" aria-hidden />}
                >
                    {i18n.t('menu_library_add_dish_save')}
                </LoadingOverlayButton>
            </Box>
            ) : null}

            {mountCourseGroupDialog ? (
                <MenuLibraryCourseGroupDialog
                    open={
                        courseGroupDialogOpen &&
                        (isCloudCourseGroupMode ? !!courseGroupCloudDishId : courseGroupDishItemId > 0)
                    }
                    onClose={() => {
                        setCourseGroupDialogOpen(false);
                        setPendingCourseGroupItemId(null);
                    }}
                    dishItemId={courseGroupDishItemId}
                    menuData={state.detailFirstMenu != null ? [state.detailFirstMenu] : []}
                    pickerMenusAll={state.courseGroupPickerMenusAll}
                    pickerMenusLoading={state.courseGroupPickerMenusLoading}
                    rbpStringId={rbpId}
                    moduleCurrencyName={state.getModuleCurrencyName()}
                    mode={isCloudCourseGroupMode ? 'cloud' : 'local'}
                    cloudMenuId={state.detailCloudMenuId ?? state.createDialogPersistedCloudMenuId ?? undefined}
                    cloudDishId={courseGroupCloudDishId || undefined}
                    initialCourseGroups={pendingCloudCourseGroups}
                    onCloudSave={isCloudCourseGroupMode ? handleCloudCourseGroupSave : undefined}
                    cloudPickerMenus={state.cloudCourseGroupPickerMenus}
                    cloudPickerMenusLoading={state.cloudCourseGroupPickerMenusLoading}
                    cloudPickerDetailLoadingId={state.cloudCoursePickerDetailLoadingId}
                    onEnsureCloudPickerMenus={
                        isCloudCourseGroupMode
                            ? () => state.ensureCloudCourseGroupPickerMenusLoaded()
                            : undefined
                    }
                    onLoadCloudPickerMenuDetail={
                        isCloudCourseGroupMode
                            ? (id) => state.loadCloudCourseGroupPickerMenuDetail(id)
                            : undefined
                    }
                />
            ) : null}
        </Box>
    );
});
